import axios, { AxiosError } from "axios"

import { toLocalDateStr, type AppliesPrecision, type TimestampDisplay } from "../utils/dates"
import type { CollectionConfig } from "../types/collections"

const API_BASE = import.meta.env.VITE_API_BASE
export const API_HOST = API_BASE.split("//")[1]

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
})

/** A full page load of `/login`, so no signed-in page stays mounted. */
export const goToLogin = (query: string = ""): void => {
    window.location.href = `${window.location.protocol}//${window.location.host}/login${query}`
}

api.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        if (window.location.pathname === "/login") {
            return Promise.reject(error)
        }
        goToLogin()
      }
      return Promise.reject(error)
    }
  )

/** The close code a socket gets when its session is gone mid-conversation:
 *  signed out, or the account was deleted on another device. */
const WS_SESSION_INVALID_CODE = 1008

/** The socket counterpart of the 401 interceptor above: a chat or voice
 *  socket closed for a dead session sends the page to `/login` too. */
export const goToLoginIfSessionClosed = (event: CloseEvent): void => {
    if (event.code !== WS_SESSION_INVALID_CODE) return
    if (window.location.pathname === "/login") return
    goToLogin()
}

// Types
export interface LoginRequest {
  email: string
  password: string
}

export type User = {
  id: number
  firstName: string | null
  // todo: Add custom isShadowUser field since
  // we're using hacks like checking the email or first name,
  // which break if those invariants change due to new features
  // like phone login.
  email: string | null
  emailValidated: boolean
  isAdmin: boolean
  /** The owner account, which can't be deleted from the Account page. */
  isOwner?: boolean
  profilePicURL: string | null
  /** How this user wants a checkin's time of day rendered on the timeline.
   *  Sticky server-side, so it follows them across devices and origins. Rides
   *  on the user object because the timeline needs it on first paint. */
  timestampDisplay: TimestampDisplay
  /** Whether they have ever *chosen* that mode, rather than being left on the
   *  default. Not derivable from the mode: "Not at all" is a real choice and
   *  it stores `none`, exactly like an untouched setting. It gates the
   *  one-shot auto-set that follows the first time they put a time on a
   *  checkin. */
  timestampDisplayChosen: boolean
}

export interface LoginResponse {
  user: User
}

export interface LogoutResponse {
  success: boolean
}

export const getUser = async (): Promise<User | null> => {
    const response = await api.get<{ user: User | null }>("/api/get_user")
    return response.data.user
}

export const postLogin = async (data: LoginRequest): Promise<LoginResponse> => {
  const body = new URLSearchParams({ email: data.email, password: data.password })
  const response = await api.post<LoginResponse>("/api/login", body, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    }
  })
  console.log("in postlogin resopnse", response.data)
  return response.data
}

export const postLogout = async (): Promise<LogoutResponse> => {
  const response = await api.post<LogoutResponse>("/api/logout")
  return response.data
}

export interface RequestLoginLinkRequest {
  email: string
  recaptcha_token: string
}

/** Deletes the signed-in account. The server checks the phrase too, and
 *  clears the session cookie on success. */
export const postDeleteAccount = async (confirmation: string): Promise<{ deleted: boolean }> => {
    const response = await api.post<{ deleted: boolean }>("/api/account/delete", { confirmation })
    return response.data
}

export const postRequestLoginLink = async (data: RequestLoginLinkRequest): Promise<{ ok: boolean }> => {
  const response = await api.post<{ ok: boolean }>(
    "/api/request_login_link",
    new URLSearchParams(data as any),
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
  )
  return response.data
}

export interface LoginLinkCodeRequest {
  email: string
  code: string
}

/**
 * Sign in with the code from a login-link email. `next` is where to go
 * afterwards: "/" once logged in, "/finish-signup" for a new address, or
 * "/login?login_link_email_taken=true". The session cookie comes with it.
 */
export const postLoginLinkCode = async (data: LoginLinkCodeRequest): Promise<{ next: string }> => {
  const response = await api.post<{ next: string }>(
    "/api/login_link/code",
    new URLSearchParams(data as any),
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
  )
  return response.data
}

export const getPendingSignupInfo = async (): Promise<{ email: string }> => {
  const response = await api.get<{ email: string }>("/api/pending_signup_info")
  return response.data
}

export interface FinishSignupFromLinkRequest {
  first_name: string
  last_name?: string
}

export const postFinishSignupFromLink = async (data: FinishSignupFromLinkRequest): Promise<{ success: boolean }> => {
  const params = new URLSearchParams()
  params.set("first_name", data.first_name)
  if (data.last_name) params.set("last_name", data.last_name)
  const response = await api.post<{ success: boolean }>(
    "/api/finish_signup_from_link",
    params,
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
  )
  return response.data
}

export const getConfig = async () => {
    const response = await api.get("/api/config")
    return response.data
}

export const updateConfig = async (configJson: string) => {
    const formData = new FormData()
    formData.append("config_json", configJson)
    return await api.post("/api/config", formData)
}

// Features Types (previously Goals)
export enum FeatureStatus {
  ACTIVE = "active",
  COMPLETED = "completed", 
  ARCHIVED = "archived"
}

export enum TaskStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  ARCHIVED = "archived"
}

export enum ManualOverrideType {
  KEEP_VALUE = "keep_value",
  DELETE = "delete"
}

/** Who set an overridden row. Only `assistant` is ever stored: a user override
 *  leaves it null. Read it through `ownerOf` in `utils/checkinFeatures`. */
export type OverrideActor = 'user' | 'assistant'

export interface Feature {
  id: number
  user_id: number
  title: string
  description?: string
  unit?: string
  config?: Record<string, any>
  start_date?: string
  end_date?: string
  status: FeatureStatus
  created_at: string
  updated_at: string
  project_id?: number
  project?: {
    id: number
    title: string
    emoji?: string
  }
}

export interface FeatureCreate {
  title: string
  description?: string
  unit?: string
  config?: Record<string, any>
  start_date?: string
  end_date?: string
  project_id: number
}

export interface Task {
  id: number
  user_id: number
  text_body: string
  due_date?: string
  due_at?: string
  status: TaskStatus
  created_at: string
  updated_at: string
  project_details?: {
    id: number
    title: string
    emoji?: string
  }[]
}

export interface TaskCreate {
  text_body: string
  due_date?: string
  due_at?: string
}

export interface CheckinFeature {
  feature_id: number;
  decimal_value?: number;
  freeform_value?: string;
  reference_value?: number;
  latitude?: number;
  longitude?: number;
  // Map feature key half. Exactly one is set on a `type: 'map'` row;
  // all three are absent on every other row.
  key_freeform_value?: string;
  key_decimal_value?: number;
  key_reference_value?: number;
  manual_override?: ManualOverrideType;
  override_actor?: OverrideActor | null;
}

export interface Checkin {
  id: number;
  user_id: number;
  body: string;
  /** Day-granular, "YYYY-MM-DD". The column behind it is a datetime, but the
   *  time of day — when the checkin has one — arrives separately in
   *  `applies_at`, so this stays a plain date everywhere it is read. */
  applies_to: string;
  /** Time of day, "HH:MM:SS", or null/absent when the checkin has none —
   *  which is most of them. `null` and `"00:00:00"` are different things:
   *  the second is a deliberate midnight. */
  applies_at?: string | null;
  /** How that time was set. `'none'` exactly when `applies_at` is null.
   *  Nothing renders it; it is echoed back on save so an edit that never
   *  touched the time cannot restate an `approximate` one as `exact`. */
  applies_precision?: AppliesPrecision;
  created_at: string;
  updated_at: string;
  // Bumped server-side by every parse that writes. Echoed back on save as
  // `base_parse_generation` so a payload assembled before a parse landed can't
  // delete the rows that parse produced.
  parse_generation?: number;
  checkin_features: CheckinFeature[];
  feature_details: Feature[];
  checkin_projects: CheckinProject[];
  project_details?: Project[];
}

export interface CheckinsListResponse {
  checkins: Checkin[];
  collection_items: Record<string, CollectionItemWithCollection>;
  tasks?: Task[];
}

export interface CollectionItem {
  id: number;
  collection_id: number;
  data: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CollectionItemWithCollection {
  id: number;
  collection_id: number;
  data: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  collection: {
    name: string;
    description: string | null;
    config: CollectionConfig;
  };
}

export interface CheckinParseResponse {
  checkin: Checkin;
  collection_items: Record<string, CollectionItemWithCollection>;
}

export interface LlmQuotaError {
  scope: 'provider' | 'user_minute' | 'user_day';
  used: number;
  limit: number;
  window_seconds: number;
  message: string;
}

// Pulls a structured LlmQuotaError out of a 429 thrown by an endpoint that calls
// a model inline, or out of a `quota_error` message on a job's watch. One extractor for every transport, so the QuotaBanner has one
// path regardless of entry point.
export const extractQuotaError = (err: unknown): LlmQuotaError | null => {
  if (err instanceof ParseQuotaError) {
    return err.quota;
  }
  const axErr = err as AxiosError<{ detail?: LlmQuotaError | string }>;
  if (axErr?.isAxiosError && axErr.response?.status === 429) {
    const detail = axErr.response.data?.detail;
    if (detail && typeof detail === 'object' && 'scope' in detail) {
      return detail as LlmQuotaError;
    }
  }
  return null;
};

// Pull a backend's plain-string `detail` off an Axios error, when present.
// Used so save-path failures (e.g. the enforce_as_set "does not allow
// repetitions" 400, value-type mismatches, unknown collection items) surface
// the server's specific message instead of a generic "Failed to save".
// Returns null for non-Axios errors or structured (non-string) details so
// callers can fall back to their generic copy.
export const extractDetail = (err: unknown): string | null => {
  const axErr = err as AxiosError<{ detail?: unknown }>;
  if (axErr?.isAxiosError) {
    const detail = axErr.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }
  return null;
};

/** One check-in's parse job from a bulk re-analyze. `deduped` when the
 *  check-in was already being parsed and this is that parse's job. */
interface BulkParseJob {
  checkin_id: number;
  job_id: string;
  deduped: boolean;
}

export interface BulkParseResponse {
  jobs: BulkParseJob[];
  failed_checkins: Array<{checkin_id: number; error: string}>;
}

export interface CheckinProject {
  project_id: number;
  manual_override?: ManualOverrideType;
  override_actor?: OverrideActor | null;
}

export interface CheckinCreate {
  body: string;
  applies_to: string;
  /** See Checkin.applies_at. Omitted or null means the checkin carries no time
   *  of day; sending it clears any time the checkin had. */
  applies_at?: string | null;
  /** How the time was set. Omitted on a checkin that has one means `exact` —
   *  a specific time with no stated provenance is a specific time — so a
   *  caller that sets `applies_at` from a bucket word or a computation has to
   *  say so. */
  applies_precision?: AppliesPrecision;
  features: CheckinFeature[];
  projects?: CheckinProject[];
  user_timezone?: string;
  // The `parse_generation` this payload's feature list was built from; see
  // Checkin.parse_generation. Omitted by callers that aren't echoing parser
  // output, which the server treats as authoritative.
  base_parse_generation?: number;
}

// Feature API Functions
export const getFeatures = async (status?: FeatureStatus): Promise<Feature[]> => {
  try {
    const params = status ? { status } : {}
    const response = await api.get<Feature[]>('/api/features/list', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching features:', error)
    throw error
  }
}

export const getAllFeatures = async (includeArchived: boolean = true): Promise<Feature[]> => {
  try {
    const response = await api.get<Feature[]>('/api/features/all')
    if (!includeArchived) {
      return response.data.filter(feature => feature.status !== FeatureStatus.ARCHIVED)
    }
    return response.data
  } catch (error) {
    console.error('Error fetching all features:', error)
    throw error
  }
}

export const getFeature = async (featureId: number): Promise<Feature> => {
  try {
    const response = await api.get<Feature>(`/api/features/${featureId}`)
    return response.data
  } catch (error) {
    console.error(`Error fetching feature ${featureId}:`, error)
    throw error
  }
}

export const createFeature = async (feature: FeatureCreate): Promise<Feature> => {
  try {
    console.log('Creating feature with payload:', JSON.stringify(feature))
    const response = await api.post<Feature>('/api/features/create', feature)
    return response.data
  } catch (error) {
    console.error('Error creating feature:', error)
    if (axios.isAxiosError(error)) {
      console.error('Error response:', error.response?.data)
    }
    throw error
  }
}

export const updateFeature = async (featureId: number, feature: FeatureCreate): Promise<Feature> => {
  try {
    const response = await api.put<Feature>(`/api/features/${featureId}`, feature)
    return response.data
  } catch (error) {
    console.error(`Error updating feature ${featureId}:`, error)
    throw error
  }
}

export const updateFeatureStatus = async (featureId: number, status: FeatureStatus): Promise<Feature> => {
  try {
    const response = await api.patch<Feature>(`/api/features/${featureId}/status`, { status })
    return response.data
  } catch (error) {
    console.error(`Error updating feature ${featureId} status:`, error)
    throw error
  }
}

export const deleteFeature = async (featureId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete<{ success: boolean; message: string }>(`/api/features/${featureId}`)
    return response.data
  } catch (error) {
    console.error(`Error deleting feature ${featureId}:`, error)
    throw error
  }
}

// Task API functions
export const getProjectTasks = async (projectId: number, status?: TaskStatus): Promise<Task[]> => {
  try {
    const params = status ? { status } : {}
    const response = await api.get<Task[]>(`/api/projects/${projectId}/tasks`, { params })
    return response.data
  } catch (error) {
    console.error(`Error fetching tasks for project ${projectId}:`, error)
    throw error
  }
}

export const createTask = async (task: TaskCreate, projectId?: number): Promise<Task> => {
  try {
    const params = projectId ? { project_id: projectId } : {}
    const response = await api.post<Task>('/api/tasks/create', task, { params })
    return response.data
  } catch (error) {
    console.error('Error creating task:', error)
    throw error
  }
}

export const updateTaskStatus = async (taskId: number, status: TaskStatus): Promise<Task> => {
  try {
    const response = await api.patch<Task>(`/api/tasks/${taskId}/status`, { status })
    return response.data
  } catch (error) {
    console.error(`Error updating task ${taskId} status:`, error)
    throw error
  }
}

export const deleteTask = async (taskId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete<{ success: boolean; message: string }>(`/api/tasks/${taskId}`)
    return response.data
  } catch (error) {
    console.error(`Error deleting task ${taskId}:`, error)
    throw error
  }
}

export const createCheckin = async (checkin: CheckinCreate): Promise<Checkin> => {
  try {
    // Always send the browser's IANA tz so the server can auto-set users.timezone
    // on first contact. Backend ignores it once the column is set.
    const payload: CheckinCreate = {
      ...checkin,
      user_timezone: checkin.user_timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    const response = await api.post<Checkin>('/api/features/checkin', payload)
    return response.data
  } catch (error) {
    console.error('Error creating checkin:', error)
    throw error
  }
}

export const updateCheckin = async (checkinId: number, checkin: CheckinCreate): Promise<Checkin> => {
  try {
    const response = await api.put<Checkin>(`/api/features/checkin/${checkinId}`, checkin)
    return response.data
  } catch (error) {
    console.error(`Error updating checkin ${checkinId}:`, error)
    throw error
  }
}

/**
 * Move a checkin to a new time of day, leaving everything else alone.
 *
 * Its own route rather than an `updateCheckin` with a different `applies_at`:
 * that one carries the whole checkin, reconciles its feature rows against the
 * payload and can trigger a parse, none of which a drag-to-reorder is asking
 * for. This writes `applies_to` + `applies_to_precision` and stops — see
 * `docs/checkin_time.md`.
 *
 * `precision` has no default here on purpose. The route's own rule — an
 * unstated precision on a set time is `exact` — is the wrong answer for a
 * drag, whose time is computed rather than chosen, so the caller says which.
 */
export const retimeCheckin = async (
  checkinId: number,
  appliesAt: string,
  precision: AppliesPrecision,
  /** The new day, only when the drop crossed into another one. */
  appliesTo?: string,
): Promise<Checkin> => {
  try {
    const response = await api.put<Checkin>(`/api/features/checkin/${checkinId}/time`, {
      applies_at: appliesAt,
      applies_precision: precision,
      ...(appliesTo ? { applies_to: appliesTo } : {}),
    })
    return response.data
  } catch (error) {
    console.error(`Error retiming checkin ${checkinId}:`, error)
    throw error
  }
}

/** One checkin's new time of day inside a whole-day assignment. `null` clears
 *  the time, which the dialog's picker can express. */
export interface CheckinTimeAssignment {
  checkin_id: number
  applies_at: string | null
  /** How this row's time was set — per row, because the user can type a clock
   *  time on one and tap "evening" on the next. `'none'` when cleared. */
  applies_precision: AppliesPrecision
  /** Set only on the row a cross-day drag is moving into the day. */
  applies_to?: string
}

/**
 * Set several checkins' times in **one** request.
 *
 * Backs the time-assignment dialog, whose promise is that the day is
 * consistent afterwards: the user sets these times against each other, so a
 * half-applied batch would leave an order nobody chose. The route is
 * transactional and validates every id first — one bad id writes none of them.
 */
export const retimeCheckins = async (times: CheckinTimeAssignment[]): Promise<Checkin[]> => {
  try {
    const response = await api.put<Checkin[]>('/api/features/checkins/times', { times })
    return response.data
  } catch (error) {
    console.error('Error setting checkin times:', error)
    throw error
  }
}

/** One proposed time, for one checkin. `applies_at` arrives in the wire's
 *  `"HH:MM:SS"` spelling like any other, so it goes through the same
 *  normalizer before it reaches a picker. There is no precision: a suggestion
 *  the user accepts is `inferred`, which is the client's to state at the
 *  moment of accepting. */
export interface CheckinTimeSuggestion {
  checkin_id: number
  applies_at: string
}

/**
 * Ask for a proposed time for each of a day's checkins. Writes nothing.
 *
 * Fired as the time-assignment dialog opens, so the request is in flight while
 * the user reads. The answer fills every picker at once when they press the
 * button, and only Confirm writes — through `retimeCheckins`, like any other
 * confirm.
 *
 * **It rethrows like any other call, and the dialog swallows it.** A failure
 * here is strictly quieter than a failed confirm: the confirm's failure earns
 * an error banner because the user asked for something and did not get it,
 * while a suggestion they never asked for is a button that stays disabled.
 * Ids come back only for the rows the model gave a usable time for; the rest
 * are left alone.
 */
export const suggestCheckinTimes = async (
  checkinIds: number[],
  /** The day being timed; one row may still be stored on another. */
  day?: string,
): Promise<CheckinTimeSuggestion[]> => {
  const response = await api.post<CheckinTimeSuggestion[]>(
    '/api/features/checkins/suggest-times',
    { checkin_ids: checkinIds, ...(day ? { day } : {}) },
  )
  return response.data
}

/**
 * Copy a checkin onto today. "Today" is the browser's local calendar day,
 * sent explicitly for the same reason `createCheckin` sends `applies_to` —
 * see `applyCheckinTemplate`.
 */
export const duplicateCheckin = async (checkinId: number): Promise<Checkin> => {
  try {
    const response = await api.post<Checkin>(`/api/features/checkin/${checkinId}/duplicate`, {
      applies_to: toLocalDateStr(new Date()),
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    return response.data
  } catch (error) {
    console.error(`Error duplicating checkin ${checkinId}:`, error)
    throw error
  }
}

// --- Checkin templates -----------------------------------------------------
// One-tap repeat checkins, stored server-side as full snapshots (body +
// feature rows + project rows). The body, label and signature are write-once —
// there is no rename and no edit — and `time_mode` is the single exception,
// the one thing `PATCH` can change.
//
// All five live under the `/api/features` prefix, on a router registered ahead
// of `features.py`'s `@router.get("/{feature_id}")` catch-all.

/**
 * What time the checkins made from a shortcut get.
 *
 * - `same` — the time the shortcut was saved with, carrying the *precision* it
 *   was saved with too, so a routine tapped as "morning" stays approximate.
 * - `current` — the clock at the moment the pill is tapped, precision `exact`.
 * - `none` — no time at all.
 *
 * Not a precision, despite `none` appearing in both: this says where the time
 * comes from, `applies_precision` says how far it can be trusted.
 */
export type CheckinTemplateTimeMode = 'same' | 'current' | 'none'

export interface CheckinTemplate {
  id: number
  user_id: number
  body: string
  /** The body, whitespace-collapsed and truncated to 24 chars server-side. */
  label: string
  /** Normalized body; unique per user. Two bodies that normalize alike are one template. */
  signature: string
  /** `same` for every template that has not been told otherwise. */
  time_mode: CheckinTemplateTimeMode
  /** The time of day `same` gives, `"HH:MM:SS"`, or null for none. Captured
   *  from the check-in it was saved from, editable afterwards. */
  applies_at: string | null
  /** How that stored time was arrived at. Null exactly when `applies_at` is —
   *  a template has no `none` precision, since it holds no date for an absent
   *  time to be confused with. */
  applies_precision: AppliesPrecision | null
  use_count: number
  last_used_at: string | null
  created_at: string
  updated_at: string
}

export const getCheckinTemplates = async (): Promise<CheckinTemplate[]> => {
  try {
    const response = await api.get<CheckinTemplate[]>('/api/features/checkin-templates')
    return response.data
  } catch (error) {
    console.error('Error fetching checkin templates:', error)
    throw error
  }
}

/**
 * Snapshot an existing checkin as a template.
 *
 * Idempotent on the normalized body: saving something that matches an existing
 * template returns that template with a 200 rather than failing, so "save this
 * again" is a no-op the caller can treat as success.
 */
export const createCheckinTemplate = async (checkinId: number): Promise<CheckinTemplate> => {
  try {
    const response = await api.post<CheckinTemplate>('/api/features/checkin-templates', {
      checkin_id: checkinId,
    })
    return response.data
  } catch (error) {
    console.error(`Error saving checkin ${checkinId} as a template:`, error)
    throw error
  }
}

/**
 * A shortcut's time — the only editable thing about one. Only the keys present
 * change anything: `applies_at: null` clears the stored time, an absent
 * `applies_at` leaves it alone. A precision is only accepted with a time.
 */
export interface CheckinTemplateUpdate {
  time_mode?: CheckinTemplateTimeMode
  applies_at?: string | null
  applies_precision?: AppliesPrecision
}

/**
 * Set what time this shortcut's checkins get, and/or the time `same` gives.
 *
 * Returns the refreshed row, so a caller can take the server's word for the
 * new state rather than patching its own copy.
 */
export const updateCheckinTemplate = async (
  templateId: number,
  update: CheckinTemplateUpdate,
): Promise<CheckinTemplate> => {
  try {
    const response = await api.patch<CheckinTemplate>(
      `/api/features/checkin-templates/${templateId}`,
      update,
    )
    return response.data
  } catch (error) {
    console.error(`Error updating checkin template ${templateId}:`, error)
    throw error
  }
}

/** Delete a template. Checkins already created from it are untouched. */
export const deleteCheckinTemplate = async (templateId: number): Promise<{ deleted: boolean; id: number }> => {
  try {
    const response = await api.delete<{ deleted: boolean; id: number }>(
      `/api/features/checkin-templates/${templateId}`,
    )
    return response.data
  } catch (error) {
    console.error(`Error deleting checkin template ${templateId}:`, error)
    throw error
  }
}

/**
 * Create a new checkin, dated today, from a template. No parse, no LLM.
 *
 * "Today" is the browser's local calendar day, sent explicitly for the same
 * reason `createCheckin` sends `applies_to`: the server's own `date.today()`
 * is UTC in every deployed environment, which files an evening tap on
 * tomorrow for anyone west of Greenwich.
 */
export const applyCheckinTemplate = async (templateId: number): Promise<Checkin> => {
  try {
    const response = await api.post<Checkin>(`/api/features/checkin-templates/${templateId}/apply`, {
      applies_to: toLocalDateStr(new Date()),
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    return response.data
  } catch (error) {
    console.error(`Error applying checkin template ${templateId}:`, error)
    throw error
  }
}

// --- Checkin nudges --------------------------------------------------------
// Small dismissable offers attached to one checkin. The only `kind` shipped so
// far is `template_upsell` — you pressed Duplicate, so want this as a one-tap
// pill? — but the shape is general: the parser's future follow-up questions
// are another `kind` over the same table and the same two routes.
//
// Dismissal is one bit and it is permanent. The server keeps the dismissed row
// as the block against ever raising that signature again, so there is no
// un-dismiss route and nothing here deletes a nudge.
//
// Both routes live under `/api/features`, on a router registered ahead of
// `features.py`'s `@router.get("/{feature_id}")` catch-all.

export interface CheckinNudge {
  id: number
  user_id: number
  /** The checkin this nudge renders beneath. */
  checkin_id: number
  /** `template_upsell` today; treat an unrecognized kind as something to skip. */
  kind: string
  /** Kind-specific; `template_upsell` carries `{ body, label }`. */
  payload: Record<string, unknown> | null
  /** Normalized body — the same identity `CheckinTemplate.signature` uses. */
  signature: string
  state: 'active' | 'dismissed'
  created_at: string
}

/**
 * This user's active nudges, highest priority first.
 *
 * Already filtered server-side: dismissed nudges never appear, and neither
 * does one whose signature matches a template the user has already saved.
 */
export const getCheckinNudges = async (): Promise<CheckinNudge[]> => {
  try {
    const response = await api.get<CheckinNudge[]>('/api/features/checkin-nudges')
    return response.data
  } catch (error) {
    console.error('Error fetching checkin nudges:', error)
    throw error
  }
}

/** Dismiss a nudge permanently. Its signature never nudges again. */
export const dismissCheckinNudge = async (nudgeId: number): Promise<CheckinNudge> => {
  try {
    const response = await api.post<CheckinNudge>(
      `/api/features/checkin-nudges/${nudgeId}/dismiss`,
    )
    return response.data
  } catch (error) {
    console.error(`Error dismissing checkin nudge ${nudgeId}:`, error)
    throw error
  }
}

/** What one repetition scan did. Only `nudges_created` drives the UI. */
export interface RepetitionScanResult {
  /** False when the cost gate or an error stopped the scan. */
  ran: boolean
  /** Which of those it was — for the console, not for the user. */
  reason: string
  scanned: number
  clusters: number
  nudges_created: number
  used_model: boolean
  /** Set when the scan was queued as a job; the result is that job's. */
  job_id?: string | null
}

/**
 * Ask the repetition judge to look for check-ins that keep saying the same
 * thing, and to raise a nudge for each cluster it finds.
 *
 * Fired as an extra step AFTER the Checkins page has loaded and rendered, and
 * nothing on screen waits for it. Most calls return `ran: false` within a
 * couple of indexed reads — the cost gate is the common path — and the
 * backend answers 200 even when the scan itself falls over, so a caller has
 * nothing to handle beyond the usual network failure.
 *
 * When there is something new to look at, the backend queues the scan as a
 * job and answers with its `job_id`; this watches the job and resolves with
 * the job's result, so the caller sees one answer either way.
 */
export const scanCheckinRepetition = async (): Promise<RepetitionScanResult> => {
  try {
    const response = await api.post<RepetitionScanResult>(
      '/api/features/checkin-repetition/scan',
    )
    const queued = response.data
    if (!queued.job_id) return queued
    const { result } = await watchJobActivity(queued.job_id)
    return { ...queued, ...(result as Partial<RepetitionScanResult> | null) }
  } catch (error) {
    console.error('Error scanning checkins for repetition:', error)
    throw error
  }
}

export const getCheckins = async (featureId?: number, projectId?: number, limit: number = 10, textSearch?: string, sortOrder?: string, startDate?: string, endDate?: string, collectionItemId?: number): Promise<CheckinsListResponse> => {
  try {
    const params: Record<string, any> = { limit }
    if (featureId) params.feature_id = featureId
    if (projectId) params.project_id = projectId
    if (textSearch) params.text_search = textSearch
    if (collectionItemId) params.collection_item_id = collectionItemId
    if (sortOrder) {
      // Always send the order if it's specified, let the backend handle defaults
      params.order = sortOrder
    }
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate

    const response = await api.get<CheckinsListResponse>('/api/features/checkins', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching checkins:', error)
    throw error
  }
}

export const getCheckinsByIds = async (checkin_ids: number[]): Promise<CheckinsListResponse> => {
  try {
    const response = await api.post<CheckinsListResponse>('/api/features/checkins/by-ids', checkin_ids)
    return response.data
  } catch (error) {
    console.error('Error fetching checkins by IDs:', error)
    throw error
  }
}

export const discussCheckinsInGenie = async (checkin_ids: number[]): Promise<{ id: number }> => {
  const response = await api.post<{ id: number }>('/api/genie/chats/discuss-checkins', { checkin_ids })
  return response.data
}

export const searchCollectionItems = async (query: string, limit: number = 10): Promise<CollectionItem[]> => {
  try {
    const params = { query, limit }
    const response = await api.get<CollectionItem[]>('/api/collections/items/search', { params })
    return response.data
  } catch (error) {
    console.error('Error searching collection items:', error)
    throw error
  }
}

export const getCollectionItem = async (itemId: number): Promise<CollectionItem> => {
  try {
    const response = await api.get<CollectionItem>(`/api/collections/items/${itemId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching collection item:', error)
    throw error
  }
}

export const deleteCheckin = async (checkinId: number): Promise<{success: boolean, message: string}> => {
  try {
    const response = await api.delete<{success: boolean, message: string}>(`/api/features/checkin/${checkinId}`)
    return response.data
  } catch (error) {
    console.error(`Error deleting checkin ${checkinId}:`, error)
    throw error
  }
}

export const deleteCheckins = async (checkinIds: number[]): Promise<{success: boolean, message: string, deleted_count?: number}> => {
  try {
    const response = await api.delete<{success: boolean, message: string, deleted_count?: number}>(`/api/features/checkins`, {
      data: checkinIds
    })
    return response.data
  } catch (error) {
    console.error('Error deleting checkins:', error)
    throw error
  }
}

// --- Parse activity and its terminal errors ----------------------------------
//
// A check-in's parse runs as a job (`parseCheckinViaJob` below) and is watched
// on the job socket, whose events are what the panel folds. These are the
// event shape and the errors a parse ends in.

/** One event off the stream. Flat, with a `type`, a `seq` and a monotonic `at`. */
export interface ParseActivityEvent {
  type: string
  seq: number
  at: number
  [key: string]: unknown
}

/**
 * A token cap hit during a parse. Carries the same structured payload the REST
 * path returns as a 429 detail, so `extractQuotaError` recognizes both and the
 * QuotaBanner needs no second path.
 */
export class ParseQuotaError extends Error {
  quota: LlmQuotaError
  constructor(quota: LlmQuotaError) {
    super(quota.message)
    this.name = 'ParseQuotaError'
    this.quota = quota
  }
}

/** Any other way a parse ends badly. `code` distinguishes them. */
class ParseSocketError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'ParseSocketError'
    this.code = code
  }
}

/**
 * Which funnel is asking for this parse. Stamped on the parse's
 * `checkin_parse_jobs` row, which the parse-quality corpus reads: the composer
 * parsing a note as it is written and the ⋮ menu re-running an old one share
 * this route but are not the same evidence, so they do not share a label. The
 * server accepts only these two — `bulk_parse` and `genie_create` are not the
 * client's to claim.
 */
type ParseTrigger = 'composer' | 'reanalyze'

export interface ParseSocketOptions {
  /** Every activity event, in arrival order. Feeds the fold. */
  onActivity?: (event: ParseActivityEvent) => void
  /**
   * The watch socket, the moment it is created. Whoever takes it owns closing
   * it — the watch closes on its own terminal message, but a caller that
   * supersedes this parse, or unmounts, closes it early through this handle.
   * Closing it stops the watching, not the parse.
   */
  onSocket?: (socket: WebSocket) => void
}

/**
 * The stored record of a check-in's latest parse, as
 * `GET /api/features/checkin/{id}/parse-activity` returns it.
 *
 * The job watch is the live path; this is the other half — a parse that has
 * ended, read with nothing but a check-in id. The events come back in the same
 * shape the watch sends them, so they go through the same fold and there is no
 * second one.
 */
export interface StoredParseActivity {
  checkin_id: number
  /** The parse the events belong to, or null when none was ever stored. */
  run_id: string | null
  events: ParseActivityEvent[]
  /** `ok` / `error` / `loop_exhausted` once it finished, `running` while it is
   *  still going, `none` when this check-in has no stored parse. */
  status: string
  dropped: number
}

export const fetchParseActivity = async (
  checkinId: number
): Promise<StoredParseActivity> => {
  const response = await api.get<StoredParseActivity>(
    `/api/features/checkin/${checkinId}/parse-activity`
  )
  return response.data
}

export type JobStatus = 'queued' | 'running' | 'ok' | 'error' | 'rate_limited' | 'cancelled'

/**
 * A background job's stored event record, as `GET /api/jobs/{id}/activity`
 * returns it: the stored-parse shape, so it folds with the same fold, plus the
 * job's own id and row status. `checkin_id` is set when the job is about a
 * check-in; `run_id` is the job id.
 */
export interface StoredJobActivity extends Omit<StoredParseActivity, 'checkin_id'> {
  job_id: string
  checkin_id: number | null
  /** The job row's status — authoritative for whether it is still live. */
  job_status: JobStatus
}

/** A job as its owner sees it — not its lease, scope, dedupe key or payload. */
export interface JobSummary {
  job_id: string
  kind: string
  subject_type: string | null
  subject_id: number | null
  status: JobStatus
  priority: number
  attempts: number
  max_attempts: number
  run_after: string
  error_code: 'rate_limited' | 'loop_exhausted' | 'failed' | 'cancelled' | null
  error: Record<string, unknown> | null
  result: Record<string, unknown> | null
  started_at: string | null
  finished_at: string | null
  created_at: string
  updated_at: string
}

/** The caller's jobs in this environment, newest first. */
export const listJobs = async (
  filters: { status?: JobStatus[]; subjectType?: string; subjectId?: number } = {}
): Promise<JobSummary[]> => {
  const params = new URLSearchParams()
  for (const s of filters.status ?? []) params.append('status', s)
  if (filters.subjectType) params.set('subject_type', filters.subjectType)
  if (filters.subjectId !== undefined) params.set('subject_id', String(filters.subjectId))
  const query = params.toString()
  const response = await api.get<{ jobs: JobSummary[] }>(
    query ? `/api/jobs?${query}` : '/api/jobs'
  )
  return response.data.jobs
}

/**
 * A job's stored record. `scope` matters only to an admin reading another
 * user's job, and names the `runner_scope` it lives in (default: the server's).
 */
export const fetchJobActivity = async (
  jobId: string,
  scope?: string
): Promise<StoredJobActivity> => {
  const response = await api.get<StoredJobActivity>(
    `/api/jobs/${encodeURIComponent(jobId)}/activity`,
    { params: scope ? { scope } : undefined }
  )
  return response.data
}

/** A job as `/admin/jobs` sees it: whose, which scope, and who holds it. */
export interface AdminJob extends JobSummary {
  /** The row id — the page's record key and the list's paging cursor. */
  id: number
  user_id: number
  runner_scope: string
  dedupe_key: string | null
  lease_owner: string | null
  lease_expires_at: string | null
  root_llm_log_id: number | null
  /** Start to finish, or to now while it runs; null before it starts. */
  duration_ms: number | null
  /** Until the lease lapses, negative once it has; null with no lease. */
  lease_ms: number | null
}

export interface AdminJobScope {
  runner_scope: string
  jobs: number
  live: number
}

export interface AdminJobsPage {
  jobs: AdminJob[]
  scope: string
  /** This server's own scope — the only one whose jobs it runs. */
  current_scope: string
  scopes: AdminJobScope[]
  /** The next page's `beforeId`, or null on the last page. */
  next_before_id: number | null
}

export const listAdminJobs = async (
  filters: {
    scope?: string
    status?: JobStatus[]
    kind?: string
    beforeId?: number
    limit?: number
  } = {}
): Promise<AdminJobsPage> => {
  const params = new URLSearchParams()
  if (filters.scope) params.set('scope', filters.scope)
  for (const s of filters.status ?? []) params.append('status', s)
  if (filters.kind) params.set('kind', filters.kind)
  if (filters.beforeId !== undefined) params.set('before_id', String(filters.beforeId))
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  const query = params.toString()
  const response = await api.get<AdminJobsPage>(
    query ? `/api/admin/jobs?${query}` : '/api/admin/jobs'
  )
  return response.data
}

/** Admin: cancel a queued or running job. A running one stops at its runner's
 *  next heartbeat. 409 when it is already over. */
export const cancelJob = async (jobId: string, scope: string): Promise<AdminJob> => {
  const response = await api.post<{ job: AdminJob }>(
    `/api/jobs/${encodeURIComponent(jobId)}/cancel`,
    null,
    { params: { scope } }
  )
  return response.data.job
}

/** Admin: run a finished job again, `attempts` kept. 409 while it is live. */
export const retryJob = async (jobId: string, scope: string): Promise<AdminJob> => {
  const response = await api.post<{ job: AdminJob }>(
    `/api/jobs/${encodeURIComponent(jobId)}/retry`,
    null,
    { params: { scope } }
  )
  return response.data.job
}

// --- The job watch socket ---------------------------------------------------
//
// `WS /api/jobs/ws/{jobId}` streams a job's record as it is written. Its only
// source is the store, so it works whichever server process the job runs on —
// see `backend/server/jobs/watch_socket.py`. An `activity` stream into the
// parse fold, the terminal vocabulary (`quota_error` → `ParseQuotaError`,
// `error` → `ParseSocketError`), `result_ready` for a job that finished, and
// `heartbeat`, which only keeps the connection warm.

/** What a finished job's watch resolves with. `result` is the job's small
 *  pointer to what it produced, never a payload of it. */
export interface JobResultReady {
  job_id: string
  result: Record<string, unknown> | null
}

type JobSocketMessage =
  | { type: 'activity'; event: ParseActivityEvent }
  | ({ type: 'result_ready' } & JobResultReady)
  | ({ type: 'quota_error' } & LlmQuotaError)
  | { type: 'error'; code: string; message: string }
  | { type: 'heartbeat'; job_status?: JobStatus }

const jobSocketUrl = (jobId: string, scope?: string): string => {
  // Same construction as the Genie chat socket: the page's scheme decides ws vs
  // wss, and the host is VITE_API_BASE's. Under `tailscale serve` the endpoint
  // is same-origin and Vite's `/api` proxy (ws: true) upgrades it; over plain
  // http the browser reaches the dev server directly.
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : ''
  return `${protocol}://${API_HOST}/api/jobs/ws/${encodeURIComponent(jobId)}${query}`
}

export interface JobWatchOptions {
  /** Every activity event, in `seq` order. Feeds the fold. */
  onActivity?: (event: ParseActivityEvent) => void
  /** The socket, the moment it is created; whoever takes it owns closing it. */
  onSocket?: (socket: WebSocket) => void
  /** For an admin watching another user's job: the `runner_scope` it is in. */
  scope?: string
}

export const watchJobActivity = (
  jobId: string,
  options?: JobWatchOptions
): Promise<JobResultReady> => {
  return new Promise<JobResultReady>((resolve, reject) => {
    let settled = false
    const socket = new WebSocket(jobSocketUrl(jobId, options?.scope))
    options?.onSocket?.(socket)

    const settle = (finish: () => void) => {
      if (settled) return
      settled = true
      try {
        socket.close()
      } catch {
        /* already closed */
      }
      finish()
    }

    socket.onmessage = (event) => {
      let message: JobSocketMessage
      try {
        message = JSON.parse(event.data)
      } catch {
        return
      }
      switch (message.type) {
        case 'activity':
          try {
            options?.onActivity?.(message.event)
          } catch (err) {
            console.error('job activity handler threw', err)
          }
          break
        case 'result_ready':
          settle(() => resolve({ job_id: message.job_id, result: message.result }))
          break
        case 'quota_error':
          settle(() => reject(new ParseQuotaError({
            scope: message.scope,
            used: message.used,
            limit: message.limit,
            window_seconds: message.window_seconds,
            message: message.message,
          })))
          break
        case 'error':
          settle(() => reject(new ParseSocketError(message.code, message.message)))
          break
      }
    }

    socket.onclose = () => {
      // Closing ends the watch, not the job: it runs on, and the stored read
      // or a new watch picks it up.
      settle(() => reject(new ParseSocketError(
        'closed',
        'The job connection closed before the job finished.'
      )))
    }
  })
}

// --- Chats ------------------------------------------------------------------
//
// The rebuilt agent chat at `/chats` (docs/chats.md). Every action is REST; the
// socket only watches one conversation and is owned by `useHgConversation`.

export type HgProfile = 'fast' | 'balanced' | 'thorough'
export type HgConversationStatus = 'active' | 'archived'
export type HgTurnStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'done'
  | 'error'
  | 'cancelled'
  | 'rate_limited'

export const HG_TURN_LIVE_STATUSES: readonly HgTurnStatus[] = ['queued', 'running', 'awaiting_approval']

/** One of the user's advisors (docs/chats.md §9). */
export interface HgAdvisor {
  persona_id: number
  name: string
  first_name: string
  archetype: string | null
  image_url: string | null
}

/** An advisor taking part in one chat. */
interface HgChatPersona extends HgAdvisor {
  added_by: 'user' | 'assistant'
}

export interface HgConversation {
  id: number
  title: string | null
  title_source: 'auto' | 'user'
  status: HgConversationStatus
  last_seq: number
  active_turn_id: number | null
  profile_override: HgProfile | null
  created_at: string
  updated_at: string
  last_activity_at: string
  /** The chat's advisors. On one chat's reads and its socket, not the list. */
  personas?: HgChatPersona[]
}

export interface HgChatItem {
  id: number
  seq: number
  turn_id: number | null
  item_type: string
  author_kind: 'user' | 'assistant' | 'persona' | 'system'
  author_persona_id: number | null
  call_id: string | null
  tool_name: string | null
  /** `proposal` and `approval` items: the proposal they are about. */
  proposal_id?: number | null
  /** Per item type; see docs/chats.md §6, "What each item holds". */
  content: Record<string, any> | null
  created_at: string
}

export interface HgChatTurn {
  id: number
  conversation_id: number
  trigger_kind: string
  status: HgTurnStatus
  job_id: string | null
  /** The advisor taking the turn's current step; null is the assistant. */
  speaker_persona_id?: number | null
  provider: string | null
  model: string | null
  effort: string | null
  steps: number
  error_code: string | null
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export type HgChatSocketMessage =
  | { type: 'conversation'; conversation: HgConversation }
  | { type: 'item'; item: HgChatItem }
  | { type: 'turn'; turn: HgChatTurn }
  | { type: 'activity'; event: ParseActivityEvent }
  | { type: 'delta'; turn_id: number; item_key: string; start: number; text: string }
  | ({ type: 'quota_error'; turn_id: number } & LlmQuotaError)
  | { type: 'heartbeat'; active_turn_id: number | null }
  | { type: 'error'; code: string; message?: string }

export interface HgChatItemsPage {
  conversation: HgConversation
  items: HgChatItem[]
  latest_turn: HgChatTurn | null
}

export interface HgSendResult {
  item: HgChatItem
  turn_id: number | null
  job_id: string | null
  started_turn: boolean
  deduped: boolean
}

export const chatSocketUrl = (conversationId: number, after: number): string => {
  // The job socket's construction; see `jobSocketUrl`.
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${protocol}://${API_HOST}/api/chats/ws/${conversationId}?after=${after}`
}

export const listChats = async (status: HgConversationStatus = 'active'): Promise<HgConversation[]> => {
  const response = await api.get<{ conversations: HgConversation[] }>('/api/chats', { params: { status } })
  return response.data.conversations
}

export const createChat = async (): Promise<HgConversation> => {
  const response = await api.post<{ conversation: HgConversation }>('/api/chats', {})
  return response.data.conversation
}

export const updateChat = async (
  conversationId: number,
  patch: { title?: string; status?: HgConversationStatus }
): Promise<HgConversation> => {
  const response = await api.patch<{ conversation: HgConversation }>(`/api/chats/${conversationId}`, patch)
  return response.data.conversation
}

export const fetchChatItems = async (conversationId: number, after: number = 0): Promise<HgChatItemsPage> => {
  const response = await api.get<HgChatItemsPage>(`/api/chats/${conversationId}/items`, { params: { after } })
  return response.data
}

export const sendChatMessage = async (
  conversationId: number,
  text: string,
  clientKey: string
): Promise<HgSendResult> => {
  const response = await api.post<HgSendResult>(`/api/chats/${conversationId}/messages`, {
    text,
    client_key: clientKey,
  })
  return response.data
}

export const cancelChatTurn = async (turnId: number): Promise<{ cancelled: boolean; turn: HgChatTurn }> => {
  const response = await api.post<{ cancelled: boolean; turn: HgChatTurn }>(`/api/chats/turns/${turnId}/cancel`)
  return response.data
}

export const listChatAdvisors = async (): Promise<HgAdvisor[]> => {
  const response = await api.get<{ advisors: HgAdvisor[] }>('/api/chats/advisors')
  return response.data.advisors
}

export const addChatPersona = async (conversationId: number, personaId: number): Promise<HgConversation> => {
  const response = await api.post<{ conversation: HgConversation }>(`/api/chats/${conversationId}/personas`, {
    persona_id: personaId,
  })
  return response.data.conversation
}

export const removeChatPersona = async (conversationId: number, personaId: number): Promise<HgConversation> => {
  const response = await api.delete<{ conversation: HgConversation }>(
    `/api/chats/${conversationId}/personas/${personaId}`,
  )
  return response.data.conversation
}

/** One change in a proposal's preview (docs/chats.md §4). `date`, `time` and
 *  `body` are the check-in as it is now, or as a new one will be; `change`
 *  is what an edit does to it. */
export interface HgProposalChange {
  key: string
  op: 'create' | 'update' | 'delete'
  checkin_id?: number
  date: string
  time?: string
  body: string
  change?: {
    body?: string
    date?: string
    time?: string | null
    tags_added?: string[]
    tags_removed?: string[]
    tags_kept?: string[]
  }
  generated?: boolean
  /** A new check-in whose text another check-in that day still has. */
  repeats?: boolean
  /** Dated after now: a later day, or later today. */
  future?: boolean
  /** A new check-in's tags as the user asked for them, checked once it is tagged. */
  tag_instruction?: string
}

/** A project, feature, collection, item or summary change on a card
 *  (`hg_chats/tools/structure.py`). */
export interface HgStructureChange {
  key: string
  op: 'create' | 'update' | 'delete'
  /** `settings`: the user's settings, drawn as one line per change. */
  entity: 'project' | 'feature' | 'collection' | 'item' | 'summary' | 'settings'
  title: string
  emoji?: string
  /** `kind: 'date'` is an ISO date the card formats. */
  fields?: Array<{ label: string; value: string | number | boolean; kind?: 'date' }>
  changes?: Array<{ label: string; from: string | null; to: string | null; kind?: 'date' }>
  consequences?: string[]
  /** The others depend on it: drawn without a checkbox. */
  required?: boolean
  /** An item whose label is already in its collection. */
  repeats?: boolean
  /** The entity's id, when it exists already. */
  id?: number
  collection?: string
  collection_id?: number
}

export interface HgProposalPreview {
  counts: Partial<Record<'create' | 'update' | 'delete', number>>
  items: Array<HgProposalChange | HgStructureChange>
  selection_readback?: string
  notes?: string[]
  /** One line under the changes, for the user. */
  footnote?: string
}

export interface HgDecisionResult {
  proposal: { id: number; status: string; approved_subset: string[] | null }
  item: HgChatItem
  turn_id: number | null
  job_id: string | null
  started_turn: boolean
}

export const decideChatProposal = async (
  proposalId: number,
  decision: 'approve' | 'reject',
  approvedSubset?: string[],
): Promise<HgDecisionResult> => {
  const response = await api.post<HgDecisionResult>(`/api/chats/proposals/${proposalId}/decision`, {
    decision,
    approved_subset: approvedSubset ?? null,
  })
  return response.data
}

/** One item as the admin inspector sees it: every column, whether or not
 * the page or the model is shown the item. */
export interface HgInspectedItem extends HgChatItem {
  provider: string | null
  in_model_context: number
  in_ui: number
  content_bytes: number
  blob_s3_key: string | null
  proposal_id: number | null
}

export interface HgInspectedTurn extends HgChatTurn {
  role: string | null
  profile: HgProfile | null
  start_seq: number | null
  consumed_seq: number | null
  end_seq: number | null
  prompt_tokens: number
  cached_tokens: number
  completion_tokens: number
  reasoning_tokens: number
  root_llm_log_id: number | null
  error: unknown
}

export interface HgChatInspection {
  conversation: HgConversation & { user_id: number }
  items: HgInspectedItem[]
  turns: HgInspectedTurn[]
  proposals: Record<string, any>[]
}

export const inspectChat = async (conversationId: number): Promise<HgChatInspection> => {
  const response = await api.get<HgChatInspection>(`/api/admin/chats/${conversationId}`)
  return response.data
}

export const getChatProfile = async (): Promise<HgProfile> => {
  const response = await api.get<{ profile: HgProfile }>('/api/users/me/chat-profile')
  return response.data.profile
}

export const putChatProfile = async (profile: HgProfile): Promise<HgProfile> => {
  const response = await api.put<{ profile: HgProfile }>('/api/users/me/chat-profile', { profile })
  return response.data.profile
}

// --- The parse as a job -----------------------------------------------------
//
// A check-in's parse is run by the server's job runner and watched on the job
// socket — which is what lets a reload mid-parse pick the indicator back up.
// See docs/parse_activity.md §2.

/**
 * Stage A's answer, when `event` is the `stage_a` event a parse job writes.
 *
 * It is an event in the record, which the fold ignores and this lifts out —
 * the project ids for the optimistic chips, the status words for the collapsed
 * line.
 */
export const stageAFromEvent = (
  event: ParseActivityEvent
): { projectIds: number[]; statusWords: string[] } | null => {
  if (event?.type !== 'stage_a') return null
  const ids = Array.isArray(event.project_ids) ? event.project_ids : []
  const words = Array.isArray(event.status_words) ? event.status_words : []
  return {
    projectIds: ids.filter((id): id is number => typeof id === 'number'),
    statusWords: words.filter((w): w is string => typeof w === 'string'),
  }
}

/** How many times a parse that found another parse of its check-in running
 *  waits that one out and asks again before giving up. */
const PARSE_JOB_BUSY_RETRIES = 3

const startParseJob = async (
  checkinId: number,
  content: string,
  trigger: ParseTrigger,
  genieMessageId?: number
): Promise<{ jobId: string } | { busyWith: string | null }> => {
  try {
    const response = await api.post<{ job_id: string }>(
      `/api/features/checkin/${checkinId}/parse-job`,
      genieMessageId === undefined
        ? { content, trigger }
        : { content, trigger, genie_message_id: genieMessageId }
    )
    return { jobId: response.data.job_id }
  } catch (err) {
    const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
      ?.response
    if (response?.status === 409) {
      const detail = response.data?.detail as { job_id?: string | null } | undefined
      return { busyWith: detail?.job_id ?? null }
    }
    if (response?.status === 404) {
      throw new ParseSocketError('not_found', 'Checkin not found')
    }
    throw err
  }
}

/**
 * Parse one check-in as a job and watch it: Stage A's answer through
 * `onStageAComplete`, every activity event through the feed, the watch socket
 * through `onSocket`, resolving with the parsed check-in and rejecting with
 * `ParseQuotaError` / `ParseSocketError`.
 *
 * `genieMessageId` is old Genie's chat message that created the check-in; the
 * server refreshes that message's snapshot before the job reports done.
 *
 * The parse's result does not ride the watch — `result_ready` only says it is
 * done — so the check-in is fetched once it is. A parse of this check-in
 * already running with other text is waited out (through the same feed, so the
 * panel shows it) and this one asked for again; one with the same text is
 * simply watched.
 */
export const parseCheckinViaJob = async (
  checkinId: number,
  content: string,
  trigger: ParseTrigger,
  options?: ParseSocketOptions & {
    onStageAComplete?: (projectIds: number[], statusWords: string[]) => void
    genieMessageId?: number
  }
): Promise<CheckinParseResponse> => {
  const watchOptions: JobWatchOptions = {
    onSocket: options?.onSocket,
    onActivity: (event) => {
      const stageA = stageAFromEvent(event)
      if (stageA) {
        try {
          options?.onStageAComplete?.(stageA.projectIds, stageA.statusWords)
        } catch (err) {
          console.error('onStageAComplete threw', err)
        }
        return
      }
      options?.onActivity?.(event)
    },
  }

  let jobId: string | null = null
  for (let attempt = 0; jobId === null; attempt++) {
    const started = await startParseJob(checkinId, content, trigger, options?.genieMessageId)
    if ('jobId' in started) {
      jobId = started.jobId
      break
    }
    if (attempt >= PARSE_JOB_BUSY_RETRIES) {
      throw new ParseSocketError('busy', 'This checkin is already being analyzed.')
    }
    if (started.busyWith) {
      // Its outcome is not ours to report; only its end matters here.
      await watchJobActivity(started.busyWith, watchOptions).catch(() => undefined)
    }
  }

  await watchJobActivity(jobId, watchOptions)
  const fresh = await getCheckinsByIds([checkinId])
  const checkin = fresh.checkins.find((c) => c.id === checkinId)
  if (!checkin) {
    throw new ParseSocketError('not_found', 'Checkin not found')
  }
  return { checkin, collection_items: fresh.collection_items }
}

export interface ParseFeedbackPayload {
  rating?: 'up' | 'down' | null
  note?: string
}

export const submitParseFeedback = async (
  checkinId: number,
  feedback: ParseFeedbackPayload
): Promise<{ id: number; parse_job_id: number | null }> => {
  try {
    const response = await api.post<{ id: number; parse_job_id: number | null }>(
      `/api/features/checkin/${checkinId}/parse-feedback`,
      feedback
    )
    return response.data
  } catch (error) {
    console.error(`Error submitting parse feedback for checkin ${checkinId}:`, error)
    throw error
  }
}

/**
 * Queue a re-analyze of each check-in, one parse job apiece, and return the
 * jobs to watch (`watchJob`). Returns once they are queued, not parsed.
 */
export const bulkParseCheckins = async (checkinIds: number[]): Promise<BulkParseResponse> => {
  try {
    const response = await api.post<BulkParseResponse>(
      `/api/features/checkins/bulk-parse`,
      { checkin_ids: checkinIds }
    )
    return response.data
  } catch (error) {
    console.error(`Error bulk parsing checkins:`, error)
    throw error
  }
}

export const searchFeatures = async (query: string): Promise<Feature[]> => {
  try {
    const response = await api.post<Feature[]>('/api/features/search', { query })
    return response.data
  } catch (error) {
    console.error('Error searching features:', error)
    throw error
  }
}

export interface UnifiedSearchItem {
  id: number
  type: 'project' | 'feature' | 'place' | 'person'
  title: string
  description?: string
  // Project-specific fields
  emoji?: string
  // Feature-specific fields
  project_id?: number
  project_title?: string
  project_emoji?: string
  unit?: string
  // Place-specific fields. `id` doubles as the collection_item_id.
  address?: string
  lat?: number
  lng?: number
  google_place_id?: string
  // Person-specific fields. `id` doubles as the collection_item_id.
  first_name?: string
  last_name?: string
  nickname?: string
  relation?: string
  /** Full People-collection data payload, so the picker can render avatars
   *  and differentiating-field subtitles without a follow-up read. */
  data?: Record<string, any>
  /** Usage signals (tombstone-gated): distinct check-ins referencing this
   *  person, and the `applies_to` date of the latest one. */
  checkin_count?: number
  last_tagged_at?: string | null
}

export interface UnifiedSearchResponse {
  items: UnifiedSearchItem[]
}

export const unifiedSearch = async (query: string): Promise<UnifiedSearchResponse> => {
  try {
    const response = await api.post<UnifiedSearchResponse>('/api/features/unified-search', { query })
    return response.data
  } catch (error) {
    console.error('Error performing unified search:', error)
    throw error
  }
}

/** Create a stub Person (first/last name only) and return its id + the full
 *  data payload so callers can drop it into a page-level cache without a
 *  follow-up read. Always creates — there is no automatic dedup. */
export interface FindOrCreatePersonResponse {
  collection_item_id: number
  data: Record<string, any>
}
export const findOrCreatePerson = async (
  firstName: string,
  lastName?: string | null,
): Promise<FindOrCreatePersonResponse> => {
  const response = await api.post<FindOrCreatePersonResponse>(
    '/api/people/find_or_create',
    { first_name: firstName, last_name: lastName ?? null },
  )
  return response.data
}

export const getRecentFeatures = async (): Promise<Feature[]> => {
  try {
    const response = await api.get<Feature[]>('/api/features/recent')
    return response.data
  } catch (error) {
    console.error('Error fetching recent features:', error)
    throw error
  }
}

/** Returns the user-global Locations feature, bootstrapping it on first call.
 *  Used by the inline composer to attach a location row when no Locations
 *  feature is yet on the checkin. */
export const getLocationsSystemFeature = async (): Promise<Feature> => {
  const response = await api.get<Feature>('/api/features/system/locations')
  return response.data
}

/** Returns the user-global People feature, bootstrapping it (and the People
 *  collection) on first call. Used by the inline composer to attach a person
 *  row when no People feature is yet on the checkin. */
export const getPeopleSystemFeature = async (): Promise<Feature> => {
  const response = await api.get<Feature>('/api/features/system/people')
  return response.data
}

// Coaching Squad Types and API Functions
export enum PersonaType {
  CELEBRITY = "celebrity",
  PERSONAL_RELATION = "personal_relation",
  USER_CUSTOM = "user_custom",
  ADMIN_CUSTOM = "admin_custom"
}

interface PersonaExpertise {
  category_id: number
  category_name: string
  category_slug: string
  expertise_level: string
}

export interface Persona {
  id: number
  first_name: string
  last_name: string | null
  bio: string
  interaction_style: string
  owner_id: number | null
  profile_picture_url: string | null
  nickname?: string | null
  expertise?: PersonaExpertise[]
  type?: PersonaType | null
  archetype?: string | null
  parent_persona_id?: number | null
  isInProject?: boolean
}

export interface UserPersona {
  persona_id: number
  persona: Persona
}

export const getPersonas = async (): Promise<Persona[]> => {
  try {
    const response = await api.get<Persona[]>('/api/coaching/personas')
    return response.data
  } catch (error) {
    console.error('Error fetching personas:', error)
    throw error
  }
}

export const searchPersonas = async (query: string): Promise<Persona[]> => {
  try {
    const response = await api.get<Persona[]>('/api/coaching/personas/search', {
      params: { query }
    })
    return response.data
  } catch (error) {
    console.error('Error searching personas:', error)
    throw error
  }
}

export const addPersonaToSquad = async (personaId: number): Promise<UserPersona> => {
  try {
    const response = await api.post<UserPersona>('/api/coaching/squad', { persona_id: personaId })
    return response.data
  } catch (error) {
    console.error('Error adding persona to squad:', error)
    throw error
  }
}

// Projects Types
export enum ProjectStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  COMPLETED = "completed",
  ARCHIVED = "archived"
}

export interface ProjectCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  confidence_score?: number;
  manual_override?: ManualOverrideType;
}

export interface Project {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  emoji?: string;
  cover_pic_s3_key?: string;
  start_date?: string;
  end_date?: string;
  config?: Record<string, any>;
  status: ProjectStatus;
  created_at: string;
  updated_at: string;
  categories?: ProjectCategory[];
}

export interface ProjectCreate {
  title: string;
  description?: string;
  emoji?: string;
  cover_pic_s3_key?: string;
  start_date?: string;
  end_date?: string;
  config?: Record<string, any>;
}

// Project API Functions
export const getProjectCategories = async (): Promise<ProjectCategory[]> => {
  try {
    const response = await api.get<ProjectCategory[]>('/api/projects/categories/list');
    return response.data;
  } catch (error) {
    console.error('Error fetching project categories:', error);
    throw error;
  }
};

export const updateProjectCategoryOverride = async (
  projectId: number,
  categoryId: number,
  manualOverride: ManualOverrideType | null
): Promise<void> => {
  try {
    await api.patch(`/api/projects/${projectId}/categories/${categoryId}/override`, {
      category_id: categoryId,
      manual_override: manualOverride
    });
  } catch (error) {
    console.error('Error updating project category override:', error);
    throw error;
  }
};

export const getProjects = async (status?: ProjectStatus): Promise<Project[]> => {
  try {
    const params = status ? { status } : {};
    const response = await api.get<Project[]>('/api/projects/list', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getProject = async (projectId: number): Promise<Project> => {
  try {
    const response = await api.get<Project>(`/api/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching project ${projectId}:`, error);
    throw error;
  }
};

export const createProject = async (project: ProjectCreate): Promise<Project> => {
  try {
    const response = await api.post<Project>('/api/projects/create', project);
    return response.data;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const updateProject = async (projectId: number, project: ProjectCreate): Promise<Project> => {
  try {
    const response = await api.put<Project>(`/api/projects/${projectId}`, project);
    return response.data;
  } catch (error) {
    console.error(`Error updating project ${projectId}:`, error);
    throw error;
  }
};

export const updateProjectStatus = async (projectId: number, status: ProjectStatus): Promise<Project> => {
  try {
    const response = await api.patch<Project>(`/api/projects/${projectId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error updating project ${projectId} status:`, error);
    throw error;
  }
};

export const deleteProject = async (projectId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete<{ success: boolean; message: string }>(`/api/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting project ${projectId}:`, error);
    throw error;
  }
};

interface ProjectSummaryTheme {
  short: string;
  full: string;
}

export interface ProjectSummary {
  project_id: number;
  themes: ProjectSummaryTheme[];
  lessons: string[];
  squad_thoughts: string[];
  created_at: string;
  updated_at: string;
  last_checkin_id?: number;
}

// Get AI-generated summary for a project
export const getProjectSummary = async (projectId: number): Promise<ProjectSummary | null> => {
  try {
    const response = await api.get<ProjectSummary | null>(`/api/projects/${projectId}/summary`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching summary for project ${projectId}:`, error);
    throw error;
  }
};

// Project association functions

export const cloneFeatureToProject = async (projectId: number, featureId: number): Promise<{success: boolean, message: string, new_feature_id: number}> => {
  try {
    const response = await api.post<{success: boolean, message: string, new_feature_id: number}>(`/api/projects/${projectId}/clone-feature/${featureId}`);
    return response.data;
  } catch (error) {
    console.error(`Error cloning feature to project ${projectId}:`, error);
    throw error;
  }
};

export const getProjectFeatures = async (projectId: number): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(`/api/projects/${projectId}/features`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching features for project ${projectId}:`, error);
    throw error;
  }
};

// Project persona association functions
export const addPersonaToProject = async (projectId: number, personaId: number): Promise<{success: boolean, message: string}> => {
  try {
    const response = await api.post<{success: boolean, message: string}>(`/api/projects/${projectId}/personas`, { persona_id: personaId });
    return response.data;
  } catch (error) {
    console.error(`Error adding persona to project ${projectId}:`, error);
    throw error;
  }
};

export const removePersonaFromProject = async (projectId: number, personaId: number): Promise<{success: boolean, message: string}> => {
  try {
    const response = await api.delete<{success: boolean, message: string}>(`/api/projects/${projectId}/personas/${personaId}`);
    return response.data;
  } catch (error) {
    console.error(`Error removing persona from project ${projectId}:`, error);
    throw error;
  }
};

// New interface for the categorized personas response
export interface CategorizedPersonas {
  project_personas: Persona[];
  bench_personas: Persona[];
  all_personas: Persona[];
}

// Get personas categorized by their relationship to a project
export const getProjectPersonasCategories = async (projectId: number): Promise<CategorizedPersonas> => {
  try {
    const endpoint = `/api/coaching/project_personas/${projectId}`;
    const response = await api.get<CategorizedPersonas>(endpoint);
    return response.data;
  } catch (error) {
    console.error(`Error fetching categorized personas for project ${projectId}:`, error);
    throw error;
  }
};

// Squad-specific personas interface (no project_personas field)
export interface SquadPersonas {
  account_squad: Persona[];
  friends_and_family: Persona[];
  top_life_advisors: Persona[];
}

// Get personas categorized for squad view
export const getSquadPersonas = async (): Promise<SquadPersonas> => {
  try {
    const response = await api.get<SquadPersonas>('/api/coaching/squad/personas');
    return response.data;
  } catch (error) {
    console.error('Error fetching squad personas:', error);
    throw error;
  }
};

export const removePersonaFromSquad = async (personaId: number): Promise<{success: boolean}> => {
  try {
    const response = await api.delete<{success: boolean}>(`/api/coaching/squad/${personaId}`)
    return response.data
  } catch (error) {
    console.error('Error removing persona from squad:', error)
    throw error
  }
}

// This function is now obsolete as nickname is stored directly on the persona object
// and is updated via the updatePersonaDetails function

export interface PersonaUpdate {
  first_name: string
  last_name?: string | null
  bio: string
  interaction_style: string
  nickname?: string | null
  archetype?: string | null
  type?: PersonaType | null
  is_public?: boolean
}

export const createPersona = async (personaData: PersonaUpdate): Promise<Persona> => {
  try {
    const response = await api.post<Persona>('/api/coaching/personas/create', personaData)
    return response.data
  } catch (error) {
    console.error('Error creating persona:', error)
    throw error
  }
}

export const updatePersonaDetails = async (personaId: number, personaData: PersonaUpdate): Promise<Persona> => {
  try {
    const response = await api.put<Persona>(`/api/coaching/personas/${personaId}`, personaData)
    return response.data
  } catch (error) {
    console.error('Error updating persona details:', error)
    throw error
  }
}

export const updatePersonaProfilePic = async (personaId: number, file: File): Promise<{success: boolean}> => {
  try {
    const formData = new FormData()
    formData.append("profile_image", file)
    
    const response = await api.post<{success: boolean}>(`/api/coaching/personas/${personaId}/update-profile-pic`, formData)
    return response.data
  } catch (error) {
    console.error('Error updating persona profile picture:', error)
    throw error
  }
}

export const refetchPersonaImages = async (personaId: number): Promise<{
  persona_id: number
  name: string
  images_found: number
  images_stored: number
  main_image_key: string | null
  message: string
  total_images?: number
  search_offset?: number
}> => {
  try {
    const response = await api.post<{
      persona_id: number
      name: string
      images_found: number
      images_stored: number
      main_image_key: string | null
      message: string
      total_images?: number
      search_offset?: number
    }>(`/api/personas/refetch-images/${personaId}`)
    return response.data
  } catch (error) {
    console.error('Error refetching persona images:', error)
    throw error
  }
}

export interface CandidateImage {
  id: number
  image_url: string
  s3_key: string
  s3_url: string | null
  search_query: string
  search_rank: number
  is_selected: boolean
}

export const getPersonaCandidateImages = async (personaId: number): Promise<{
  persona_id: number
  candidate_images: CandidateImage[]
}> => {
  try {
    const response = await api.get<{
      persona_id: number
      candidate_images: CandidateImage[]
    }>(`/api/personas/candidate-images/${personaId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching candidate images:', error)
    throw error
  }
}

export const selectPersonaCandidateImage = async (personaId: number, candidateImageId: number): Promise<{
  success: boolean
  message: string
  s3_key: string
}> => {
  try {
    const response = await api.post<{
      success: boolean
      message: string
      s3_key: string
    }>(`/api/personas/select-candidate-image/${personaId}/${candidateImageId}`)
    return response.data
  } catch (error) {
    console.error('Error selecting candidate image:', error)
    throw error
  }
}

// Genie Chat Types and API Functions
export interface GenieChat {
  id: number
  created_at: string
  title?: string
  result_data?: any
  // Stage 5: project context for advice-kind chats so the index page can
  // group/label without a second fetch. Optional on agent chats.
  project_id?: number | null
  project_title?: string | null
  project_emoji?: string | null
}

export interface AllGenieChats {
  chats: GenieChat[]
}

// `kind` filters by `genie_chats.chat_kind` server-side. Pass 'agent' for
// the Genie history page, 'advice' for the Advice index. Defaults to no
// filter (admin/debug) when omitted.
export const getAllGenieChats = async (kind?: 'agent' | 'advice'): Promise<AllGenieChats> => {
  try {
    const params: Record<string, string> = {}
    if (kind) params.kind = kind
    const response = await api.get<AllGenieChats>('/api/genie/chats', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching all genie chats:', error)
    throw error
  }
}

export const deleteGenieChat = async (chatId: number): Promise<{success: boolean, message: string}> => {
  try {
    const response = await api.delete<{success: boolean, message: string}>(`/api/genie/chats/${chatId}`)
    return response.data
  } catch (error) {
    console.error(`Error deleting genie chat ${chatId}:`, error)
    throw error
  }
}

// -- Advice chat (Stage 5) -------------------------------------------------

// Mirrors the cloned-advisor shape Stage 3's `bootstrap_advice_chat` writes
// into `genie_chats.config.advisors`: a local `id` (0/1/2…) the moderator
// uses with `speak_as_advisor(advisor_id=…)`, plus the persona fields
// needed to render the bubble's avatar + name.
export interface AdviceChatAdvisor {
  id: number
  persona_id?: number
  first_name?: string | null
  last_name?: string | null
  nickname?: string | null
  archetype?: string | null
  bio?: string | null
  interaction_style?: string | null
  image_s3_key?: string | null
  profile_picture_url?: string | null
}

export interface AdviceChatMessage {
  id: number
  role: 'system' | 'user' | 'assistant' | 'function'
  message_type?: string | null
  content: string
  author_id?: number | null
  created_at?: string | null
}

interface AdviceChatProject {
  id: number
  title?: string | null
  emoji?: string | null
  description?: string | null
}

export interface AdviceChatPayload {
  chat: {
    id: number
    title?: string | null
    chat_kind: string
    created_at?: string | null
    updated_at?: string | null
    project?: AdviceChatProject | null
    advisors: AdviceChatAdvisor[]
  }
  messages: AdviceChatMessage[]
}

export const getAdviceChat = async (chatId: number | string): Promise<AdviceChatPayload> => {
  const response = await api.get<AdviceChatPayload>(`/api/genie/chats/${chatId}/advice`)
  return response.data
}

// -- Triggered-advice + advisor-suggestions reads (Stages 3+4 already
// shipped the endpoints; these are the Stage 6 surface's client stubs,
// landed here in Stage 5 per the plan-mandated client.ts changes).

export interface TriggeredAdviceLatest {
  id: number
  chat_id: number
  trigger_reason: string
  judged_verdict?: string | null
  created_at: string
  dismissed_at?: string | null
  chat_title?: string | null
}

export const getProjectTriggeredAdviceLatest = async (
  projectId: number,
): Promise<TriggeredAdviceLatest | null> => {
  const response = await api.get<TriggeredAdviceLatest | null>(
    `/api/projects/${projectId}/triggered-advice/latest`,
  )
  return response.data
}

// Note: the backend endpoint is `/triggered-advice/{advice_id}/dismiss`.
// Named `dismissTriggeredAdvice` per the plan; second arg is the
// triggered_advice row id.
export const dismissTriggeredAdvice = async (
  projectId: number,
  adviceId: number,
): Promise<{ status: string; id: number }> => {
  const response = await api.post<{ status: string; id: number }>(
    `/api/projects/${projectId}/triggered-advice/${adviceId}/dismiss`,
  )
  return response.data
}

interface AdvisorSuggestionPersona {
  id: number
  first_name?: string | null
  last_name?: string | null
  bio?: string | null
  archetype?: string | null
  nickname?: string | null
  image_s3_key?: string | null
  profile_picture_url?: string | null
}

interface AdvisorSuggestionDraft {
  first_name?: string
  last_name?: string
  bio?: string
  interaction_style?: string
  archetype?: string
  expertise_categories?: string[]
}

export interface AdvisorSuggestion {
  id: number
  rationale?: string | null
  status: string
  created_at?: string | null
  kind: 'persona' | 'draft'
  persona?: AdvisorSuggestionPersona
  draft?: AdvisorSuggestionDraft
}

export const getProjectAdvisorSuggestions = async (
  projectId: number,
): Promise<AdvisorSuggestion[]> => {
  const response = await api.get<AdvisorSuggestion[]>(
    `/api/projects/${projectId}/advisor-suggestions`,
  )
  return response.data
}

export const acceptAdvisorSuggestion = async (
  projectId: number,
  sid: number,
): Promise<{ status: string; persona_id?: number }> => {
  const response = await api.post<{ status: string; persona_id?: number }>(
    `/api/projects/${projectId}/advisor-suggestions/${sid}/accept`,
  )
  return response.data
}

export const dismissAdvisorSuggestion = async (
  projectId: number,
  sid: number,
): Promise<{ status: string }> => {
  const response = await api.post<{ status: string }>(
    `/api/projects/${projectId}/advisor-suggestions/${sid}/dismiss`,
  )
  return response.data
}

// -- Dismissed nudges (Stage 6) -------------------------------------------
// Persists which per-project "empty squad" nudges the user has hidden.
// Backed by `users.prefs.dismissedNudges` (JSON array) via the user_prefs
// router; see backend `dismiss_nudge`.

export interface DismissedNudges {
  project_ids: number[]
}

export const getDismissedNudges = async (): Promise<DismissedNudges> => {
  const response = await api.get<DismissedNudges>(
    `/api/users/me/dismissed-nudges`,
  )
  return response.data
}

export const dismissNudge = async (projectId: number): Promise<DismissedNudges> => {
  const response = await api.post<DismissedNudges>(
    `/api/users/me/dismissed-nudges/${projectId}`,
  )
  return response.data
}

// -- Device location -------------------------------------------------------
// The user's latest browser fix, cached server-side so the parse anchor doesn't
// depend on re-asking permission. The status deliberately carries only a
// timestamp — the client's one question is "is this stale enough to re-ask",
// and the coordinates would widen the exposure for nothing.

export interface DeviceLocationStatus {
  /** ISO timestamp of the stored fix, or null if there isn't one. */
  captured_at?: string | null
  opted_out?: boolean
}

export const getDeviceLocationStatus = async (): Promise<DeviceLocationStatus> => {
  const response = await api.get<DeviceLocationStatus>(
    `/api/places/me/device-location`,
  )
  return response.data
}

export const putDeviceLocation = async (
  lat: number,
  lng: number,
  accuracyM?: number,
): Promise<DeviceLocationStatus> => {
  const response = await api.put<DeviceLocationStatus>(
    `/api/places/me/device-location`,
    { lat, lng, accuracy_m: accuracyM ?? null },
  )
  return response.data
}

export const forgetDeviceLocation = async (): Promise<DeviceLocationStatus> => {
  const response = await api.delete<DeviceLocationStatus>(
    `/api/places/me/device-location`,
  )
  return response.data
}

export interface DeviceLocationPref {
  opted_out: boolean
}

export const setDeviceLocationPref = async (
  optedOut: boolean,
): Promise<DeviceLocationPref> => {
  const response = await api.put<DeviceLocationPref>(
    `/api/users/me/device-location-pref`,
    { opted_out: optedOut },
  )
  return response.data
}

// -- Timestamp display mode ------------------------------------------------
//
// Write-only: the current mode arrives on the user object (`User.timestampDisplay`),
// which the app already loads before it renders anything, so there is nothing
// to GET here.
//
// Two writes, because a choice and a follow are different acts. Both answer
// with the state they left behind, so the caller can put it straight on the
// cached user object — which it has to, since there is no GET to refetch from
// and the timeline reads the mode off that object.

export interface TimestampDisplayState {
  mode: TimestampDisplay
  chosen: boolean
}

/** An explicit choice, from the account page, the sort menu or a date tap.
 *  Always marks the setting chosen, `none` included. */
export const setTimestampDisplay = async (
  mode: TimestampDisplay,
): Promise<TimestampDisplayState> => {
  const response = await api.put<TimestampDisplayState>(
    `/api/users/me/timestamp-display`,
    { mode },
  )
  return response.data
}

/** Report how the user just set a time on a checkin, and let the server
 *  decide whether that is the one-shot auto-set. A no-op for anyone who has
 *  chosen already; the response is the effective state either way. */
export const reportCheckinTimeSet = async (
  precision: AppliesPrecision,
): Promise<TimestampDisplayState> => {
  const response = await api.post<TimestampDisplayState>(
    `/api/users/me/timestamp-display/from-time-set`,
    { precision },
  )
  return response.data
}

// -- One-time UX moments ---------------------------------------------------
// "Show this to the user exactly once, ever": permission primers, first-run
// explanations. Backed by `users.prefs.uxMoments` (key → ISO timestamp of
// first display) via the user_prefs router.
//
// Server-side on purpose. localStorage is per-origin and per-device, and one
// person reaches this app from localhost, from the box's three HTTPS clone
// origins, and from their phone — a lifetime dialog kept locally would fire
// once per origin per device.
//
// Every moment we define lives in UX_MOMENT_KEYS, so there is one list to read
// when adding the next one. Keys are kebab-case and scoped by area.

export const UX_MOMENT_KEYS = {
  /** Why we're about to ask the browser for location, shown before the
   *  permission prompt so a cold "Deny" doesn't cost us the capability. */
  locationPermissionPrimer: 'location-permission-primer',
} as const

export type UxMomentKey = (typeof UX_MOMENT_KEYS)[keyof typeof UX_MOMENT_KEYS]

export interface UxMoments {
  /** Moment key → ISO timestamp it was FIRST shown. Re-marking won't move it. */
  seen: Record<string, string>
}

export const getUxMoments = async (): Promise<UxMoments> => {
  const response = await api.get<UxMoments>(`/api/users/me/ux-moments`)
  return response.data
}

export const markUxMoment = async (key: UxMomentKey): Promise<UxMoments> => {
  const response = await api.post<UxMoments>(`/api/users/me/ux-moments/${key}`)
  return response.data
}

export type LlmUsageWindow = '24h' | '7d' | '30d'

interface LlmUsageTotal {
  prompt_tokens: number
  completion_tokens: number
  cached_tokens: number
  reasoning_tokens: number
  calls: number
}

// Shared shape of the per-model / per-call-type breakdowns. Both respect the
// selected user filter, so their rows sum to `total.calls`. Both also group by
// `reasoning_effort`, so a model or call type that ran at two efforts in the
// window appears as two rows; null means no reasoning parameter was sent.
export interface LlmUsageBreakdown {
  reasoning_effort: string | null
  prompt_tokens: number
  completion_tokens: number
  cached_tokens: number
  reasoning_tokens: number
  calls: number
}

interface LlmUsageByModel extends LlmUsageBreakdown {
  provider: string | null
  model: string | null
}

interface LlmUsageByCallType extends LlmUsageBreakdown {
  call_type: string | null
}

export interface LlmUsageByUser {
  user_id: number | null
  name: string | null
  email: string | null
  prompt_tokens: number
  completion_tokens: number
  cached_tokens: number
  calls: number
}

export interface LlmUsageByUserResponse {
  window: LlmUsageWindow
  q: string
  offset: number
  limit: number
  hide_test: boolean
  // Users matching the filter in the window, not rows on this page.
  matched: number
  rows: LlmUsageByUser[]
}

interface LlmUsageBucket {
  bucket: string
  prompt_tokens: number
  completion_tokens: number
}

export interface LlmUsageResponse {
  window: LlmUsageWindow
  bucket: 'hour' | 'day'
  user_id: number | null
  // The user the whole response is filtered to, for the page's headings. Null
  // when nothing is selected, and also when the selected id has no `users` row
  // — llm_logs outlives a deleted user.
  selected_user: { user_id: number; name: string | null; email: string | null } | null
  // Whether test accounts were left out. Narrows every number in the response
  // together, so the breakdowns still reconcile against `total`.
  hide_test: boolean
  total: LlmUsageTotal
  by_model: LlmUsageByModel[]
  by_call_type: LlmUsageByCallType[]
  time_series: LlmUsageBucket[]
  // The same buckets split one dimension at a time, long format. Bucket
  // totals match `time_series`' input + output, so switching the chart's
  // split-by never changes the totals under it.
  time_series_by_model: LlmUsageSplitBucket[]
  // Keyed "<model>: <effort>". Effort alone would average over models with
  // different prices and different caps, so it is never a series on its own.
  time_series_by_model_effort: LlmUsageSplitBucket[]
  // Peak rolling-`rolling_seconds` spend per bucket, keyed by split. The only
  // measure a rate cap can be drawn against: a bucket total says nothing
  // about any single minute inside it.
  rate_series: {
    total: LlmUsageRateBucket[]
    model: LlmUsageRateBucket[]
    model_effort: LlmUsageRateBucket[]
  }
  rolling_seconds: number
  model_caps: { model: string; cap: number }[]
}

interface LlmUsageRateBucket {
  bucket: string
  series_key: string | null
  peak: number
}

export interface LlmUsageSplitBucket {
  bucket: string
  // null for an effort never sent; rendered as "none", not dropped.
  series_key: string | null
  tokens: number
}

// `hideTest` drops accounts flagged `users.is_test`, plus rows whose user no
// longer exists — see `docs/test_accounts.md` for why the second is folded into
// the first. It is not offered on the headroom endpoint: test traffic consumes
// rate-limit capacity like any other.
export const getLlmUsage = async (
  window: LlmUsageWindow,
  userId?: number | null,
  hideTest?: boolean,
): Promise<LlmUsageResponse> => {
  const params: Record<string, string | number | boolean> = { window }
  if (userId != null) params.user_id = userId
  if (hideTest) params.hide_test = true
  const response = await api.get<LlmUsageResponse>('/api/admin/llm-usage', { params })
  return response.data
}

// The per-user breakdown pages and filters on the server: the 30-day window
// covers thousands of distinct ids, most of them deleted test users whose log
// rows outlive them. `q` matches name or email as a substring and the id
// exactly.
export const getLlmUsageByUser = async (
  opts: {
    window: LlmUsageWindow
    q?: string
    offset?: number
    limit?: number
    hideTest?: boolean
  },
): Promise<LlmUsageByUserResponse> => {
  const params: Record<string, string | number | boolean> = {
    window: opts.window,
    offset: opts.offset ?? 0,
    limit: opts.limit ?? 25,
  }
  if (opts.q) params.q = opts.q
  if (opts.hideTest) params.hide_test = true
  const response = await api.get<LlmUsageByUserResponse>(
    '/api/admin/llm-usage/by-user', { params },
  )
  return response.data
}

// Live capacity for one (provider, model) over the rolling 60s rate window.
// Independent of the LLM Usage page's 24h/7d/30d selector, which is why it
// comes from its own endpoint.
export interface LlmModelHeadroom {
  provider: string
  model: string | null
  // The live 60s window — what the limiter would check right now.
  used_now: number
  // The worst rolling-60s window in the selected range, and when it happened.
  peak: number
  peak_at: string | null
  cap: number | null
  // `peak` as a share of `cap`: how close traffic has actually come.
  pct_of_cap: number | null
  upstream_declared: number | null
  upstream_observed: number | null
  upstream_requests_observed: number | null
  observed_at: string | null
  // The cap in our code disagreeing with what the provider reports.
  upstream_mismatch: boolean
}

export interface LlmRateHeadroomResponse {
  rolling_seconds: number
  window: LlmUsageWindow
  bucket: 'hour' | 'day'
  models: LlmModelHeadroom[]
}

export const getLlmRateHeadroom = async (
  window: LlmUsageWindow,
): Promise<LlmRateHeadroomResponse> => {
  const response = await api.get<LlmRateHeadroomResponse>(
    '/api/admin/llm-usage/headroom',
    { params: { window } },
  )
  return response.data
}

export interface LlmRecentCall {
  id: number
  created_at: string | null
  user_id: number | null
  name: string | null
  email: string | null
  provider: string | null
  model: string | null
  response_id: string | null
  call_type: string | null
  prompt_tokens: number | null
  cached_tokens: number | null
  cache_creation_tokens: number | null
  completion_tokens: number | null
  reasoning_tokens: number | null
  // The routing decision behind this call. Null on Anthropic (outside the
  // routing table) and on rows predating it.
  reasoning_effort: string | null
  route_key: string | null
  token_count: number | null
  duration_ms: number | null
  success: boolean | null
  system_prompt: string | null
  user_prompt: string | null
  response: string | null
  error_message: string | null
  parent_log_id: number | null
  // Only present on root rows. Agentic-loop iterations beyond the first are
  // nested here; non-loop calls have an empty array.
  children?: LlmRecentCall[]
}

export interface LlmRecentCallsResponse {
  calls: LlmRecentCall[]
  next_before_id: number | null
}

export const getRecentLlmCalls = async (
  opts: {
    limit?: number
    userId?: number | null
    beforeId?: number | null
    hideTest?: boolean
  } = {},
): Promise<LlmRecentCallsResponse> => {
  const params: Record<string, string | number | boolean> = { limit: opts.limit ?? 50 }
  if (opts.userId != null) params.user_id = opts.userId
  if (opts.beforeId != null) params.before_id = opts.beforeId
  if (opts.hideTest) params.hide_test = true
  const response = await api.get<LlmRecentCallsResponse>('/api/admin/llm-usage/recent', { params })
  return response.data
}

// ---------------------------------------------------------------------------
// Admin single-user management
// ---------------------------------------------------------------------------

export interface AdminUserSearchResult {
  id: number
  name: string | null
  email: string | null
}

interface AdminUserUsage {
  minute_used: number
  minute_cap: number
  day_used: number
  day_cap: number
}

export interface AdminUser {
  id: number
  name: string | null
  email: string | null
  created_at: string | null
  is_admin: boolean
  log_full_llm: boolean
  usage: AdminUserUsage
}

export const searchUsers = async (q: string): Promise<AdminUserSearchResult[]> => {
  const response = await api.get<{ results: AdminUserSearchResult[] }>(
    '/api/admin/users/search', { params: { q } },
  )
  return response.data.results
}

export const getAdminUser = async (userId: number): Promise<AdminUser> => {
  const response = await api.get<AdminUser>(`/api/admin/users/${userId}`)
  return response.data
}

export const resetUserUsage = async (userId: number): Promise<{ deleted: number }> => {
  const response = await api.post<{ status: string; deleted: number }>(
    `/api/admin/users/${userId}/reset-usage`,
  )
  return { deleted: response.data.deleted }
}

export const setUserLogFullLlm = async (
  userId: number,
  enabled: boolean,
): Promise<{ log_full_llm: boolean }> => {
  const response = await api.post<{ status: string; log_full_llm: boolean }>(
    `/api/admin/users/${userId}/log-full-llm`,
    { enabled },
  )
  return { log_full_llm: response.data.log_full_llm }
}

// ---------------------------------------------------------------------------
// Admin parse-quality dashboard (parse-edit data collection Stage 7)
// ---------------------------------------------------------------------------

export type ParseQualityWindow = '7d' | '30d' | '90d'

interface ParseQualityTotals {
  parse_jobs: number
  ok: number
  loop_exhausted: number
  error: number
  edited_within_7d: number
  edited_within_7d_pct: number | null
  edit_events: number
  genie_questions: number
  feedback: {
    total: number
    up: number
    down: number
    with_note: number
  }
  item_deletions: {
    user_explicit: number
    orphan_gc: number
  }
}

interface ParseQualityBucket {
  bucket: string
  parses: number
  edited_within_7d: number
  loop_exhausted: number
  error: number
}

interface ParseQualityTriggerRow {
  trigger: string
  parses: number
  edited_within_7d: number
  loop_exhausted: number
  error: number
}

interface ParseQualityOverrideRate {
  value_type: string
  total_rows: number
  overridden_rows: number
  override_rate: number | null
}

export interface ParseQualitySummary {
  window: ParseQualityWindow
  bucket: 'day' | 'week'
  user_id: number | null
  totals: ParseQualityTotals
  time_series: ParseQualityBucket[]
  by_trigger: ParseQualityTriggerRow[]
  override_rates_by_feature_type: ParseQualityOverrideRate[]
}

export const getParseQualitySummary = async (
  window: ParseQualityWindow,
  userId?: number | null,
): Promise<ParseQualitySummary> => {
  const params: Record<string, string | number> = { window }
  if (userId != null) params.user_id = userId
  const response = await api.get<ParseQualitySummary>('/api/admin/parse-quality/summary', { params })
  return response.data
}

export type ParseQualityEventKind = 'edits' | 'questions' | 'feedback' | 'deletions'

// One checkin_features row inside an edit event's pre/post image, as captured
// by parse_telemetry.diff_feature_rows.
export interface ParseQualityFeatureTitle {
  title: string
  value_type: string
  config?: Record<string, any> | null
}

export interface ParseQualityFeatureRow {
  feature_id: number
  decimal_value?: number | null
  freeform_value?: string | null
  reference_value?: number | null
  latitude?: number | null
  longitude?: number | null
  key_freeform_value?: string | null
  key_decimal_value?: number | null
  key_reference_value?: number | null
  manual_override?: string | null
  override_actor?: OverrideActor | null
}

// One checkin_projects association inside an edit event's pre/post image, as
// captured by parse_telemetry.diff_project_rows. Absent from the post image
// when the tag was removed outright; manual_override 'delete' is the
// untag tombstone.
interface ParseQualityProjectRow {
  project_id: number
  manual_override?: string | null
}

interface ParseQualityEditImage {
  changed_feature_ids?: number[]
  rows?: ParseQualityFeatureRow[]
  // Older captures may use these keys instead of `rows`.
  pre_rows?: ParseQualityFeatureRow[]
  post_rows?: ParseQualityFeatureRow[]
  // Present only when the save changed parser-owned project tags.
  changed_project_ids?: number[]
  projects?: ParseQualityProjectRow[]
}

// Union row for the events feed; the populated fields depend on `kind`.
export interface ParseQualityEvent {
  id: number
  created_at: string | null
  user_id: number | null
  name: string | null
  email: string | null
  checkin_id: number | null
  // edits + questions + feedback
  parse_job_id?: number | null
  body?: string | null
  parse_trigger?: string | null
  parse_status?: string | null
  // edits + questions
  source?: string | null
  genie_chat_id?: number | null
  bulk_edit_journal_id?: number | null
  body_changed?: boolean
  pre_image?: ParseQualityEditImage | null
  post_image?: ParseQualityEditImage | null
  // questions
  question?: string | null
  // feedback
  rating?: 'up' | 'down' | null
  note?: string | null
  // deletions
  collection_id?: number
  collection_name?: string
  item_id?: number
  item_data?: Record<string, unknown> | null
  item_metadata?: Record<string, unknown> | null
  deleted_by?: 'user_explicit' | 'orphan_gc'
  gc_context?: string | null
  reference_count_at_delete?: number | null
}

export interface ParseQualityEventsResponse {
  kind: ParseQualityEventKind
  events: ParseQualityEvent[]
  next_before_id: number | null
  feature_titles: Record<string, ParseQualityFeatureTitle>
  project_titles: Record<string, string>
}

export const getParseQualityEvents = async (
  opts: {
    kind: ParseQualityEventKind
    limit?: number
    userId?: number | null
    source?: string | null
    beforeId?: number | null
  },
): Promise<ParseQualityEventsResponse> => {
  const params: Record<string, string | number> = {
    kind: opts.kind,
    limit: opts.limit ?? 50,
  }
  if (opts.userId != null) params.user_id = opts.userId
  if (opts.source != null) params.source = opts.source
  if (opts.beforeId != null) params.before_id = opts.beforeId
  const response = await api.get<ParseQualityEventsResponse>('/api/admin/parse-quality/events', { params })
  return response.data
}

// ---------------------------------------------------------------------------
// Activity summaries (Stage 5 backend contract: backend/server/summaries)
// ---------------------------------------------------------------------------

export type SummaryKind =
  | 'single_feature_stats'
  | 'stratified_single'
  | 'streaks'
  | 'logging_rate'
  | 'period_over_period'
  | 'outliers'
  | 'project_narrative'
  | 'paired_same_day'
  | 'paired_lagged'
  | 'threshold_cross'
  | 'rolling_window'
  | 'before_after_milestone'

export type SummaryTier = 'week' | 'month' | 'year'
type SummaryScope = 'feature' | 'project' | 'all'
export type SummaryGapFill = 'none' | 'zero' | 'carry_forward' | 'interpolate'
type SummaryStatus = 'active' | 'archived'

/**
 * A persisted `summary_configs` row (mirrors backend SummaryConfigRead).
 *
 * `pin_surfaces` is a freeform JSON object on the backend. Since the Stage 17
 * load model, feature/project pages load summaries by SOURCE membership (see
 * `summariesForFeature` / `summariesForProject`), so the only meaningful pin is
 * the dashboard opt-in: `{ dashboard?: boolean }` (see `dashboardSummaries`).
 * Older configs may still carry vestigial `feature_page`/`project_page` flags;
 * they are ignored.
 */
export interface SummaryConfig {
  id: number
  user_id: number
  kind: SummaryKind
  scope: SummaryScope
  source_feature_ids: number[]
  source_project_ids: number[]
  user_title: string | null
  user_description: string | null
  tiers: SummaryTier[]
  gap_fill_policy: SummaryGapFill
  params: Record<string, any> | null
  recompute_policy: Record<string, any> | null
  pin_surfaces: Record<string, any> | null
  status: SummaryStatus
  created_at: string
  updated_at: string
}

/** Numeric-panel period granularities. Supersets SummaryTier with two
 *  read-only windows ('6m', 'all') that carry no precomputed text. */
export type SummaryWindow = SummaryTier | '6m' | 'all'

export interface SummaryNumericResponse {
  config_id: number
  kind: SummaryKind
  /** The precomputed-text tier this window maps to, or null for 6m/all. */
  tier: SummaryTier | null
  /** The window the panel was computed for, when requested via the picker. */
  window: SummaryWindow | null
  period_start: string
  period_end: string
  is_empty: boolean
  /** System-generated technical label, e.g. "Single-feature stats — Calories". */
  label: string
  numeric_payload: Record<string, any> | null
}

/** Result of `GET /api/summaries/results` — 200 in both cases. */
export type SummaryResultResponse =
  | {
      exists: true
      id: number
      config_id: number
      tier: SummaryTier
      period_start: string | null
      period_end: string | null
      text_summary: string | null
      numeric_payload: Record<string, any> | null
      last_built_checkin_id: number | null
      last_built_token_total: number
      last_judge_verdict: 'boring' | 'notable' | 'pivotal' | null
      llm_log_id: number | null
      built_at: string | null
    }
  | {
      exists: false
      config_id: number
      tier: SummaryTier
      period_start: string
      period_end: string
    }

/** The summaries pinned to the dashboard — the one surface with explicit
 *  opt-in pinning (`pin_surfaces.dashboard`). Source-agnostic: a dashboard
 *  summary may be feature- or project-sourced. */
export const dashboardSummaries = (configs: SummaryConfig[]): SummaryConfig[] =>
  configs.filter(
    (c) => !!c.pin_surfaces?.dashboard && c.status === 'active',
  )

/** Apply a user-defined card order to a list of summaries.
 *
 *  `order` is advisory, never authoritative about membership: it orders the
 *  summaries it names and says nothing about the rest. Anything it doesn't
 *  mention — a summary created on another device, or one pinned since the last
 *  drag — keeps its natural position AFTER the ordered ones, by id, so the
 *  result is deterministic and a new card lands predictably at the end. Ids in
 *  `order` for summaries that are gone (deleted, unpinned) are simply not found
 *  and drop out.
 *
 *  That tolerance is what lets a reorder be a single independent write: it never
 *  has to be transactional with a create, a delete, or a pin. */
export const orderSummaries = (
  configs: SummaryConfig[],
  order: number[] | undefined,
): SummaryConfig[] => {
  if (!order?.length) return configs
  const rank = new Map(order.map((id, i) => [id, i]))
  const ranked = configs.filter((c) => rank.has(c.id))
  const rest = configs.filter((c) => !rank.has(c.id))
  ranked.sort((a, b) => (rank.get(a.id) as number) - (rank.get(b.id) as number))
  rest.sort((a, b) => a.id - b.id)
  return [...ranked, ...rest]
}

/** Per-surface card order (`users.prefs.summaryOrder`). Surfaces the user has
 *  never reordered are absent. */
export interface SummaryOrders {
  orders: Record<string, number[]>
}

export const getSummaryOrder = async (): Promise<SummaryOrders> => {
  const response = await api.get<SummaryOrders>('/api/users/me/summary-order')
  return response.data
}

/** Replace the order for one surface — the whole list is sent, so a drag is one
 *  write and the stored order is always internally consistent. */
export const setSummaryOrder = async (
  surface: string,
  configIds: number[],
): Promise<SummaryOrders> => {
  const response = await api.put<SummaryOrders>(
    `/api/users/me/summary-order/${surface}`,
    { config_ids: configIds },
  )
  return response.data
}

/** Load model — FEATURE page: every active summary whose source includes this
 *  feature. A feature can appear in many summaries; all show. */
export const summariesForFeature = (
  configs: SummaryConfig[],
  featureId: number,
): SummaryConfig[] =>
  configs.filter(
    (c) =>
      c.status === 'active' &&
      Array.isArray(c.source_feature_ids) &&
      c.source_feature_ids.includes(featureId),
  )

/** Load model — PROJECT page: every active summary the project is involved in
 *  (its `source_project_ids` contains the project) PLUS the feature-sourced
 *  summaries for the project's member features, deduped to at most ONE summary
 *  per member feature. Determinism: consider feature-sourced configs
 *  most-recently-updated first and let each claim its member features, so a
 *  feature contributes to at most one card. Project-involved summaries are
 *  always included. */
export const summariesForProject = (
  configs: SummaryConfig[],
  projectId: number,
  projectFeatureIds: number[],
): SummaryConfig[] => {
  const active = configs.filter((c) => c.status === 'active')
  const memberSet = new Set(projectFeatureIds)

  const involved = active.filter(
    (c) => Array.isArray(c.source_project_ids) && c.source_project_ids.includes(projectId),
  )

  const featureSourced = active
    .filter(
      (c) =>
        Array.isArray(c.source_feature_ids) &&
        c.source_feature_ids.some((fid) => memberSet.has(fid)),
    )
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : b.id - a.id))

  const chosen: SummaryConfig[] = []
  const claimed = new Set<number>()
  for (const c of featureSourced) {
    const members = c.source_feature_ids.filter((fid) => memberSet.has(fid))
    if (members.some((fid) => !claimed.has(fid))) {
      chosen.push(c)
      members.forEach((fid) => claimed.add(fid))
    }
  }

  // Union, deduped by id (a config could be both project-involved and
  // feature-sourced, though not in practice).
  const seen = new Set<number>()
  return [...involved, ...chosen].filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)))
}

/** How one summary card was last left: which visualization, and which period
 *  granularity. Both optional — a card may have been left on a non-default view
 *  without ever touching the picker, or vice versa. */
export interface SummaryCardPref {
  view?: string
  granularity?: string
}

/** config id (string key) → that card's remembered `{view, granularity}`.
 *  Backed by `users.prefs.summaryCardPrefs`; see `useSummaryCardPrefs`. */
export interface SummaryCardPrefs {
  cards: Record<string, SummaryCardPref>
}

export const getSummaryCardPrefs = async (): Promise<SummaryCardPrefs> => {
  const response = await api.get<SummaryCardPrefs>('/api/users/me/summary-card-prefs')
  return response.data
}

/** Merge `patch` into this card's remembered state — pass either axis or both;
 *  omitted ones keep their stored value. Returns the whole updated map. */
export const setSummaryCardPref = async (
  configId: number,
  patch: SummaryCardPref,
): Promise<SummaryCardPrefs> => {
  const response = await api.put<SummaryCardPrefs>(
    `/api/users/me/summary-card-prefs/${configId}`,
    patch,
  )
  return response.data
}

export const getSummaryConfigs = async (
  includeArchived: boolean = false,
): Promise<SummaryConfig[]> => {
  try {
    const params = includeArchived ? { include_archived: true } : {}
    const response = await api.get<SummaryConfig[]>('/api/summaries/configs', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching summary configs:', error)
    throw error
  }
}

/** Fetch the live numeric panel. Select the period by EITHER `tier`
 *  (week/month/year, snapped to the canonical start) OR `window`
 *  (week/month/year/6m/all — the feature-page picker granularity). Pass
 *  exactly one. */
export const getSummaryNumeric = async (args: {
  configId: number
  periodStart: string
  tier?: SummaryTier
  window?: SummaryWindow
}): Promise<SummaryNumericResponse> => {
  try {
    const response = await api.get<SummaryNumericResponse>('/api/summaries/numeric', {
      params: {
        config_id: args.configId,
        period_start: args.periodStart,
        ...(args.tier ? { tier: args.tier } : {}),
        ...(args.window ? { window: args.window } : {}),
      },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching summary numeric panel:', error)
    throw error
  }
}

/** A summary config's days across all time, ISO and ascending. `dates` are the
 *  days its calendar / coverage-trend views draw — on a yes/no streak or
 *  logging rate, only the counted days. `pagingDates` are the days with any
 *  data, for skip-empty paging; the same list everywhere else. */
export const getSummaryActiveDates = async (
  configId: number,
): Promise<{ dates: string[]; pagingDates: string[] }> => {
  try {
    const response = await api.get<{
      config_id: number
      dates: string[]
      paging_dates?: string[]
    }>('/api/summaries/active-dates', { params: { config_id: configId } })
    const { dates, paging_dates } = response.data
    return { dates, pagingDates: paging_dates ?? dates }
  } catch (error) {
    console.error('Error fetching summary active dates:', error)
    throw error
  }
}

export const getSummaryResult = async (args: {
  configId: number
  tier: SummaryTier
  periodStart: string
}): Promise<SummaryResultResponse> => {
  try {
    const response = await api.get<SummaryResultResponse>('/api/summaries/results', {
      params: {
        config_id: args.configId,
        tier: args.tier,
        period_start: args.periodStart,
      },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching summary result:', error)
    throw error
  }
}

/** One ISO week of the user's `period_digest` summary, shaped for reading (see
 *  `numeric_router._digest_row`).
 *
 *  `digest_mode` is how the week was written up, and it is the thing a reviewer
 *  is actually comparing:
 *    'prose'    — over the word budget, so a model summarized it.
 *    'verbatim' — under the budget, so the check-ins ARE the digest; no model.
 *    'empty'    — no check-in that week had a body; `text` is null.
 *  Rows built before the budget landed carry neither `digest_mode` nor
 *  `source_word_count`; both come back null and the surface says "unlabelled"
 *  rather than guessing. */
export interface WeekDigest {
  /** The week's Monday (YYYY-MM-DD) — the canonical `summary_results` key. */
  period_start: string
  /** The week's Sunday, INCLUSIVE (the backend's `period_bounds` contract). */
  period_end: string
  text: string | null
  digest_mode: 'prose' | 'verbatim' | 'empty' | null
  source_word_count: number | null
  checkin_count: number | null
  day_count: number | null
  built_at: string | null
}

/** `config_id` is null when the user has never had a digest config provisioned
 *  — indistinguishable, to a reader, from having no weeks built yet. */
export interface WeekDigestsResponse {
  config_id: number | null
  weeks: WeekDigest[]
}

/** Week digests whose Monday falls in `[start, end]` (both inclusive, both
 *  optional). Only weeks that have actually been built come back — a gap in the
 *  result is a week with no row, not an error. */
export const getWeekDigests = async (
  start?: string,
  end?: string,
): Promise<WeekDigestsResponse> => {
  const response = await api.get<WeekDigestsResponse>('/api/summaries/week-digests', {
    params: {
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
    },
  })
  return response.data
}

/** Result of `POST /api/summaries/explain` — an on-demand, per-view narrative
 *  generated LIVE for the current (config, view, window). Unlike the precomputed
 *  `text_summary`, this is keyed by the active view AND an arbitrary picker
 *  window, so it's computed when the user asks ("Explain") rather than at build
 *  time. */
/** Explanation voice: 'plain' is the non-technical default shown in the UI;
 *  'technical' is the terse, number-dense register kept as an option. */
export type SummaryExplainRegister = 'plain' | 'technical'

export interface SummaryExplainResponse {
  config_id: number
  view: string
  register: SummaryExplainRegister
  is_empty: boolean
  text: string | null
  llm_log_id: number | null
}

/** Generate the per-view "Explain" narrative for the current window. Select the
 *  period by EITHER `window` (the picker granularity) OR `tier`, exactly as
 *  `getSummaryNumeric`. `register` picks the voice (defaults to 'plain' server-
 *  side). `image` is a STUB for a future screenshot of the rendered unit — the
 *  backend accepts it but does not yet send it to the LLM. */
export const explainSummary = async (args: {
  configId: number
  view: string
  periodStart: string
  window?: SummaryWindow
  tier?: SummaryTier
  register?: SummaryExplainRegister
  /** STUB: base64 screenshot of the rendered unit; not yet used server-side. */
  image?: string
}): Promise<SummaryExplainResponse> => {
  try {
    const response = await api.post<SummaryExplainResponse>('/api/summaries/explain', {
      config_id: args.configId,
      view: args.view,
      period_start: args.periodStart,
      ...(args.window ? { window: args.window } : {}),
      ...(args.tier ? { tier: args.tier } : {}),
      ...(args.register ? { register: args.register } : {}),
      ...(args.image ? { image: args.image } : {}),
    })
    return response.data
  } catch (error) {
    console.error('Error fetching summary explanation:', error)
    throw error
  }
}

/** Create/update payload (mirrors backend SummaryConfigCreate/Update). The
 *  server owns user_id/status/timestamps. */
export interface SummaryConfigInput {
  kind: SummaryKind
  scope: SummaryScope
  source_feature_ids?: number[]
  source_project_ids?: number[]
  user_title?: string | null
  user_description?: string | null
  tiers?: SummaryTier[]
  gap_fill_policy?: SummaryGapFill
  params?: Record<string, any> | null
  recompute_policy?: Record<string, any> | null
  pin_surfaces?: Record<string, any> | null
}

export const createSummaryConfig = async (
  body: SummaryConfigInput,
): Promise<SummaryConfig> => {
  try {
    const response = await api.post<SummaryConfig>('/api/summaries/configs', body)
    return response.data
  } catch (error) {
    console.error('Error creating summary config:', error)
    throw error
  }
}

export const updateSummaryConfig = async (
  configId: number,
  body: Partial<SummaryConfigInput>,
): Promise<SummaryConfig> => {
  try {
    const response = await api.patch<SummaryConfig>(
      `/api/summaries/configs/${configId}`,
      body,
    )
    return response.data
  } catch (error) {
    console.error('Error updating summary config:', error)
    throw error
  }
}

// Hard-deletes the config (and, via FK cascade, its summary_results history).
// Destructive and irreversible — there is no archive/unarchive yet.
export const deleteSummaryConfig = async (
  configId: number,
): Promise<SummaryConfig> => {
  try {
    const response = await api.delete<SummaryConfig>(
      `/api/summaries/configs/${configId}`,
    )
    return response.data
  } catch (error) {
    console.error('Error deleting summary config:', error)
    throw error
  }
}

// ---------------------------------------------------------- test tracker
// Dev-only, admin-only read API over `memgenie_dev` run history. Every route
// 404s when the backend was launched without DEV_TOOLS_ENABLED.

// `error` = the harness itself never produced a report (crash, missing dev
// server); distinct from `failed`, where tests ran and went red.
type TestRunStatus = 'passed' | 'failed' | 'error'

export interface TestSuiteRun {
  id: number
  suite: string
  env: string | null
  clone: string | null
  git_sha: string | null
  started_at: string | null
  duration_ms: number | null
  passed: number
  failed: number
  skipped: number
  exit_code: number | null
  error_message: string | null
  status: TestRunStatus
}

export interface TestResult {
  id: number
  suite: string
  test_id: string
  classname: string | null
  name: string | null
  status: string
  duration_ms: number | null
  failure_message: string | null
}

export interface TestRunsResponse {
  suites: string[]
  runs: TestSuiteRun[]
}

export interface TestSuitesResponse {
  suites: { suite: string; run: TestSuiteRun | null }[]
}

// `counts` keeps `failed` and `error` apart — `suite_runs.failed` collapses them.
export interface TestRunDetail {
  run: TestSuiteRun
  counts: Record<string, number>
  tests: TestResult[]
}

export interface FlakyTest {
  suite: string
  test_id: string
  name: string | null
  runs: number
  passed: number
  failed: number
  skipped: number
  retry_flakes: number
  fail_rate: number
  reasons: ('mixed' | 'retry')[]
  sample_failure_message: string | null
  last_seen: string | null
}

export interface FlakyResponse {
  window_runs: number
  flaky: FlakyTest[]
}

export interface TestHistoryEntry {
  run_id: number
  env: string | null
  clone: string | null
  git_sha: string | null
  started_at: string | null
  status: string
  duration_ms: number | null
  failure_message: string | null
}

export interface TestHistoryResponse {
  suite: string
  test_id: string
  history: TestHistoryEntry[]
}

export const getTestTrackerSuites = async (): Promise<TestSuitesResponse> => {
  const response = await api.get<TestSuitesResponse>('/api/dev/test-tracker/suites')
  return response.data
}

export const getTestTrackerRuns = async (params?: {
  suite?: string | null
  env?: string | null
  clone?: string | null
  limit?: number
}): Promise<TestRunsResponse> => {
  const query: Record<string, string | number> = {}
  if (params?.suite) query.suite = params.suite
  if (params?.env) query.env = params.env
  if (params?.clone) query.clone = params.clone
  if (params?.limit) query.limit = params.limit
  const response = await api.get<TestRunsResponse>('/api/dev/test-tracker/runs', {
    params: query,
  })
  return response.data
}

// `statuses` maps to the repeatable `status` param — the drill-down asks for
// failures only by default, since one backend run carries ~640 rows.
export const getTestTrackerRun = async (
  runId: number,
  statuses?: string[],
): Promise<TestRunDetail> => {
  const response = await api.get<TestRunDetail>(
    `/api/dev/test-tracker/runs/${runId}`,
    {
      params: statuses?.length ? { status: statuses } : {},
      paramsSerializer: { indexes: null },
    },
  )
  return response.data
}

export const getTestTrackerFlaky = async (params?: {
  suite?: string | null
  runs?: number
}): Promise<FlakyResponse> => {
  const query: Record<string, string | number> = {}
  if (params?.suite) query.suite = params.suite
  if (params?.runs) query.runs = params.runs
  const response = await api.get<FlakyResponse>('/api/dev/test-tracker/flaky', {
    params: query,
  })
  return response.data
}

// The suites don't run in this backend — they run on the EC2 integration box,
// driven by .github/workflows/test-tracker.yml. These two speak to that
// workflow: what is already going, and launch one.

// `live` is the billed backend-live/e2e-live pair on its own — what the admin
// button asks for. `auto` is what a push does: the cheap three, plus the pair
// only if none has run in the last ~20h.
export type TestDispatchMode = 'live' | 'auto'

interface TestWorkflowRun {
  id: number
  status: string
  event: string
  created_at: string
  html_url: string
}

export interface TestDispatchStatus {
  // False when the backend has no GITHUB_DISPATCH_TOKEN, which is the normal
  // state of a dev clone — history still renders, the button just can't fire.
  configured: boolean
  modes: TestDispatchMode[]
  repo: string
  ref: string
  workflow_url: string
  in_flight: TestWorkflowRun[]
  // A GitHub hiccup listing runs; reported rather than thrown, so the page
  // stays usable and the button stays clickable.
  error: string | null
}

export const getTestTrackerDispatchStatus = async (): Promise<TestDispatchStatus> => {
  const response = await api.get<TestDispatchStatus>('/api/dev/test-tracker/dispatch')
  return response.data
}

export interface TestDispatchResult {
  dispatched: boolean
  mode: TestDispatchMode
  ref: string
  workflow_url: string
}

// 409 when a run is already in flight — a live run is ~45 minutes of billed
// model calls, so `force` is how the caller says the second one is deliberate.
export const dispatchTestTrackerRun = async (
  mode: TestDispatchMode = 'live',
  force = false,
): Promise<TestDispatchResult> => {
  const response = await api.post<TestDispatchResult>(
    '/api/dev/test-tracker/dispatch',
    { mode, force },
  )
  return response.data
}

// The `?test_id=` form: ids contain slashes, and uvicorn hands Starlette an
// already-decoded path, so the path form can't round-trip them.
export const getTestTrackerHistory = async (
  suite: string,
  testId: string,
  limit = 30,
): Promise<TestHistoryResponse> => {
  const response = await api.get<TestHistoryResponse>(
    `/api/dev/test-tracker/tests/${encodeURIComponent(suite)}/history`,
    { params: { test_id: testId, limit } },
  )
  return response.data
}

// ---------------------------------------------------------- push (iOS shell)
// The shell registers its APNs token here (`docs/native_shell.md`, "Push").
// `POST /api/push/test` exists only on a backend with DEV_TOOLS_ENABLED, which
// `test_push_available` reports.

export interface PushTokenSummary {
  platform: string
  app_id: string
  token_suffix: string
  updated_at: string | null
}

export interface PushStatus {
  tokens: PushTokenSummary[]
  test_push_available: boolean
}

export interface TestPushResult extends Omit<PushTokenSummary, 'updated_at'> {
  ok: boolean
  status: number
  reason: string | null
  removed: boolean
}

export const getPushStatus = async (): Promise<PushStatus> => {
  const response = await api.get<PushStatus>('/api/push/status')
  return response.data
}

export const registerPushToken = async (body: {
  platform: 'ios' | 'android'
  token: string
  app_id: string
}): Promise<PushStatus> => {
  const response = await api.post<PushStatus>('/api/push/register', body)
  return response.data
}

export const sendTestPush = async (): Promise<TestPushResult[]> => {
  const response = await api.post<{ results: TestPushResult[] }>('/api/push/test')
  return response.data.results
}